import { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  Chip,
  Divider,
  CircularProgress,
  TextField,
  FormControl,
  FormHelperText,
  Select,
  MenuItem,
  IconButton,
  Alert,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TablePagination,
  Menu,
} from "@mui/material";
import {
  ArrowBack,
  ArrowForward,
  ArrowDropDown,
  CheckCircle,
  Cancel,
  Edit,
  Save,
  Close,
  Visibility,
  OpenInNew,
  Download,
  AutoAwesome,
  AutoFixHigh,
  Autorenew,
  CompareArrows,
  PostAdd,
  DeleteOutline,
  CloudUpload,
  Add,
  Delete,
} from "@mui/icons-material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { formatDistanceToNow } from "date-fns";
import api, { resolveQuestionHtmlAssetUrl } from "../../api";
import CategorySaveFields from "./components/CategorySaveFields";
import GenerateSimilarPaperDialog from "./components/GenerateSimilarPaperDialog";
import JobDeleteGeneratedDialog from "./components/JobDeleteGeneratedDialog";
import SaveToBankDestinationDialog, { validateDestinationForm } from "./components/SaveToBankDestinationDialog";
import { QUESTION_TYPES } from "./components/QuestionTypeSelector";
import HtmlWithLatex from "./components/HtmlWithLatex";
import QuestionAnswerDisplay from "./components/QuestionAnswerDisplay";
import QuestionPaperLayout from "./components/QuestionPaperLayout";
import DifficultyChip from "./components/DifficultyChip";
import { questionHasRenderableAnswer as questionHasAnswer } from "./utils/questionHasRenderableAnswer";
import PassageWithLineNumbers from "./components/PassageWithLineNumbers";
import McqOptions from "./components/McqOptions";
import TrueFalseOptions from "./components/TrueFalseOptions";
import FillInBlankEditor from "./components/FillInBlankEditor";
import FillTypeMarksControls from "./components/FillTypeMarksControls";
import TableFillEditor, { DEFAULT_GRID } from "./components/TableFillEditor";
import ImageOverlayFillEditor from "./components/ImageOverlayFillEditor";
import ShortAnswerEditor from "./components/ShortAnswerEditor";
import MatchingEditor from "./components/MatchingEditor";
import OrderingEditor from "./components/OrderingEditor";
import EssayAnswer from "./components/EssayAnswer";
import InteractiveDiagramEditor from "./components/InteractiveDiagramEditor";
import SimilarityReportDialog, { SimilarityChip } from "./components/SimilarityReportDialog";
import { WebSimilarityChip } from "./components/WebSimilaritySection";
import { validateQuestion } from "./utils/questionValidators";
import {
  QUILL_MODULES,
  QUILL_FORMATS,
  QuillR2Editor,
  QUILL_MODULES as EXPL_QUILL_MODULES,
  QUILL_FORMATS as EXPL_QUILL_FORMATS,
  PASSAGE_QUILL_MODULES,
  PASSAGE_QUILL_FORMATS,
} from "../../utils/quillQuestionEditorConfig";
import {
  mergeAiSuggestion,
  withFormattedExplanation,
  AiAcceptSections,
  AiExplanationPreview,
  AI_ACCEPT_DEFAULT,
  formatAiExplanation,
} from "./QuestionBankForm";


// ── helpers ──────────────────────────────────────────────────────────────

// Entities are decoded, not just tags stripped: the generated stems are full of &nbsp; and a raw
// "Sarah&nbsp;sat&nbsp;by" reads as noise wherever this text is reused.
const stripHtml = (html) =>
  (html || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, " ")
    .trim();

/** The generator marks a slot whose figure could not be drawn — it ships without its picture. */
const questionNeedsFigure = (q) => {
  const marked = (x) => x?.visual_status === "missing";
  return marked(q) || (q?.sub_questions || []).some(marked);
};

const normalizeQuillValue = (html) => {
  if (!html) return "";
  const compact = String(html).trim();
  if (compact === "<p><br></p>" || compact === "<p></p>") return "";
  return compact;
};

/** Stem / question image URLs: prefer image_urls[], fallback image_url */
const collectImageUrls = (q) => {
  if (!q) return [];
  const arr = Array.isArray(q.image_urls) ? q.image_urls.filter(Boolean) : [];
  if (arr.length) return arr;
  return q.image_url ? [q.image_url] : [];
};

/** Worth redrawing: the slot shows a figure now, or the source question it came from did. */
const slotHasFigure = (q) =>
  !!q && (collectImageUrls(q).length > 0 || !!q.original_image_url || !!q.has_original_image);

/** The figure on this slot is still the source paper's, not one drawn for the new stem. */
const usesOriginalFigure = (q) =>
  !!q && (!!q.has_original_image || (!!q.original_image_url && q.image_url === q.original_image_url));

/** Once the reviewer changes a slot's figure by hand, the plan that reused the source paper's
 *  picture no longer holds — leaving the flags set makes the next regenerate re-attach it. */
const clearedOriginalFigureFlags = (q) =>
  q?.has_original_image || q?.freeze_visual ? { has_original_image: null, freeze_visual: null } : {};

const syncDraftImageFields = (urls) => {
  const cleaned = (urls || []).filter(Boolean);
  return {
    image_url: cleaned[0] || null,
    image_urls: cleaned.length > 0 ? cleaned : null,
  };
};

const buildVerificationSummary = (report) => {
  if (!report) return '';
  const parts = [];
  if (report.answer_gaps > 0) parts.push(`${report.answer_gaps} answer gap(s)`);
  if (report.checks?.count_mismatch) parts.push('question count mismatch');
  if (report.checks?.parity_issues?.length) parts.push(`${report.checks.parity_issues.length} parity issue(s)`);
  return parts.length ? parts.join(', ') : 'issues found';
};

/** Mirrors QuestionBankEntityCreatorService::standardMockExamName so a hand-made mock exam
 *  gets the same name shape as one built by `php artisan similar-papers:batch`. */
const standardMockExamName = (importData, jobData) => {
  const school = (importData?.school_name || "").trim();
  const rawSubject = (importData?.exam_subject || "").trim();
  const subject = rawSubject
    ? rawSubject.charAt(0).toUpperCase() + rawSubject.slice(1).toLowerCase()
    : "";
  const year = (importData?.source_filename || "").match(/\b(?:19|20)\d{2}\b/)?.[0] || "";
  const stem = [school, "11+", subject, year].filter(Boolean).join(" ").trim();
  return `${stem || `Similar Paper #${jobData?.id ?? ""}`.trim()} — Mock`;
};

const typeColors = {
  "multiple-choice": "#1976d2",
  "multiple-select": "#7b1fa2",
  "true-false": "#388e3c",
  "fill-in-the-blank": "#f57c00",
  "multi-fill": "#e64a19",
  table: "#00838f",
  "image-fill": "#00695c",
  matching: "#0097a7",
  ordering: "#5d4037",
  essay: "#c2185b",
  "short-answer": "#455a64",
  drawing: "#546e7a",
  grouped: "#6a1b9a",
};

const EDITABLE_QUESTION_TYPES = QUESTION_TYPES.filter((t) => t.value !== "grouped");

const normalizeDifficulty = (d) => {
  const map = { easy: "Easy", medium: "Medium", hard: "Hard" };
  return map[String(d || "").toLowerCase()] || "Medium";
};

const emptySubQuestion = (difficulty = "Medium") => ({
  question_text: "",
  type: "short-answer",
  difficulty: normalizeDifficulty(difficulty),
  default_marks: 1,
  topic: null,
  answer_data: { correct_answer_text: "" },
  paper_number: null,
  section_title: null,
  section_instructions: null,
  question_subtype: null,
  image_url: null,
  image_urls: null,
  image_description: null,
  explanation: "",
  options: [],
});

const defaultsForType = (type) => {
  switch (type) {
    case "multiple-choice":
      return {
        options: [
          { text: "", option_text: "", is_correct: true },
          { text: "", option_text: "", is_correct: false },
          { text: "", option_text: "", is_correct: false },
          { text: "", option_text: "", is_correct: false },
        ],
        answer_data: {},
      };
    case "multiple-select":
      return {
        options: [
          { text: "", option_text: "", is_correct: false },
          { text: "", option_text: "", is_correct: false },
          { text: "", option_text: "", is_correct: false },
          { text: "", option_text: "", is_correct: false },
        ],
        answer_data: {},
      };
    case "true-false":
      return {
        options: [
          { text: "True", option_text: "True", is_correct: true },
          { text: "False", option_text: "False", is_correct: false },
        ],
        answer_data: {},
      };
    case "fill-in-the-blank":
      return {
        options: [],
        answer_data: {
          correct_blanks: [[""]],
          blank_labels: [""],
          blank_marks: [1],
          blank_label_positions: ["before"],
        },
      };
    case "multi-fill":
      return {
        options: [],
        answer_data: {
          correct_blanks: [[""], [""]],
          blank_labels: ["", ""],
          blank_marks: [1, 1],
          blank_label_positions: ["before", "before"],
        },
      };
    case "table":
      return {
        options: [],
        answer_data: {
          correct_blanks: Array.from({ length: 9 }, () => [""]),
          blank_marks: Array.from({ length: 9 }, () => 1),
          blank_labels: [],
          table_layout: DEFAULT_GRID(),
        },
      };
    case "image-fill":
      return {
        options: [],
        answer_data: {
          correct_blanks: [[""]],
          blank_marks: [1],
          blank_labels: [],
          overlay_layout: { image_url: "", blanks: [] },
        },
      };
    case "matching":
      return {
        options: [
          { text: "", match_text: "" },
          { text: "", match_text: "" },
        ],
        answer_data: {},
      };
    case "ordering":
      return {
        options: [{ text: "" }, { text: "" }],
        answer_data: {},
      };
    case "essay":
      return { options: [], answer_data: { model_answer: "", correct_answer_text: "" } };
    case "short-answer":
      return { options: [], answer_data: { correct_answer_text: "" } };
    case "drawing":
      return { options: [], answer_data: { solution_geometry: null } };
    default:
      return {};
  }
};

const getCorrectBlanks = (draft) => {
  const cb = draft?.answer_data?.correct_blanks;
  if (Array.isArray(cb) && cb.length > 0) {
    return cb.map((b) => (Array.isArray(b) ? b : [String(b ?? "")]));
  }
  return [[""]];
};

const trueFalseValueFromDraft = (draft) => {
  const opts = draft?.options || [];
  const trueOpt = opts.find((o) => /true/i.test(String(o.text || o.option_text || "")));
  if (trueOpt) return !!trueOpt.is_correct;
  return true;
};

const hydrateDraftForEdit = (raw) => {
  const draft = JSON.parse(JSON.stringify({ ...raw, default_marks: raw.default_marks || 1 }));
  draft.difficulty = normalizeDifficulty(draft.difficulty);
  if (["fill-in-the-blank", "multi-fill"].includes(draft.type)) {
    const cb = draft.answer_data?.correct_blanks;
    if (!Array.isArray(cb) || cb.length === 0) {
      const text = draft.answer_data?.correct_answer_text || "";
      draft.answer_data = {
        ...(draft.answer_data || {}),
        correct_blanks: text ? [[text]] : [[""]],
        blank_labels: draft.answer_data?.blank_labels?.length ? draft.answer_data.blank_labels : [""],
        blank_marks: draft.answer_data?.blank_marks?.length ? draft.answer_data.blank_marks : [1],
      };
    } else {
      draft.answer_data = {
        ...(draft.answer_data || {}),
        correct_blanks: cb.map((b) => (Array.isArray(b) ? b : [String(b ?? "")])),
      };
    }
  }
  if (draft.type === "true-false" && (!Array.isArray(draft.options) || draft.options.length < 2)) {
    draft.options = defaultsForType("true-false").options;
  }
  if (
    ["multiple-choice", "multiple-select"].includes(draft.type) &&
    (!Array.isArray(draft.options) || draft.options.length < 2)
  ) {
    draft.options = defaultsForType(draft.type).options;
  }
  if (["multiple-choice", "multiple-select", "true-false"].includes(draft.type) && Array.isArray(draft.options)) {
    draft.options = draft.options.map((o) => ({
      ...o,
      text: o.text || o.option_text || "",
      option_text: o.option_text || o.text || "",
    }));
  }
  if (draft.type === "matching" && (!Array.isArray(draft.options) || draft.options.length < 2)) {
    draft.options = defaultsForType("matching").options;
  }
  if (draft.type === "ordering" && (!Array.isArray(draft.options) || draft.options.length < 2)) {
    draft.options = defaultsForType("ordering").options;
  }
  return draft;
};

/** Type / marks / difficulty / option count — the attributes a variation is meant to preserve. */
const QuestionShapeChips = ({ question }) => {
  const optionCount = Array.isArray(question?.options) ? question.options.length : 0;
  const subCount = Array.isArray(question?.sub_questions) ? question.sub_questions.length : 0;

  return (
    <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", mb: 1 }}>
      {question?.type && (
        <Chip
          label={QUESTION_TYPES.find((t) => t.value === question.type)?.label || question.type}
          size="small"
          sx={{ bgcolor: typeColors[question.type] || "#999", color: "#fff", fontSize: 11 }}
        />
      )}
      {question?.default_marks ? (
        <Chip label={`${question.default_marks} mark${question.default_marks > 1 ? "s" : ""}`} size="small" />
      ) : null}
      {question?.difficulty && <DifficultyChip difficulty={question.difficulty} />}
      {optionCount > 0 && <Chip label={`${optionCount} options`} size="small" variant="outlined" />}
      {subCount > 0 && <Chip label={`${subCount} parts`} size="small" variant="outlined" />}
    </Box>
  );
};

/**
 * One question rendered read-only, grouped-aware — QuestionPaperLayout does not render
 * sub-questions, the caller loops. Used for both panes of the compare dialog.
 */
const ReadOnlyQuestionView = ({ question, questionNumber }) => {
  if (!question) return null;

  const renderLeaf = (q, number) => (
    <>
      <QuestionPaperLayout question={q} questionNumber={number} showAnswers={false} />
      {questionHasAnswer(q) && (
        <Box sx={{ mt: 1.5, p: 1.5, bgcolor: "#f0fdf4", borderRadius: 1, border: "1px solid #bbf7d0" }}>
          <Typography variant="caption" color="success.dark" sx={{ fontWeight: 600 }}>
            Answer
          </Typography>
          <QuestionAnswerDisplay question={q} />
        </Box>
      )}
      {q.explanation && String(q.explanation).replace(/<[^>]*>/g, "").trim() && (
        <Box sx={{ mt: 1.5, p: 1.5, bgcolor: "#f8fafc", borderRadius: 1, border: "1px solid #e2e8f0" }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: "block", mb: 0.5 }}>
            Explanation
          </Typography>
          <HtmlWithLatex html={q.explanation} />
        </Box>
      )}
    </>
  );

  if (question.type === "grouped") {
    const subs = question.sub_questions || [];
    return (
      <Box>
        <QuestionShapeChips question={question} />
        <Paper sx={{ p: 1.5, mb: 1.5, bgcolor: "#fafafa", border: "1px solid #eee" }}>
          <HtmlWithLatex html={question.question_text || ""} />
          {collectImageUrls(question).map((url) => (
            <Box key={url} sx={{ mt: 1 }}>
              <img src={url} alt="Question" style={{ maxWidth: "100%", maxHeight: 300, borderRadius: 4 }} />
            </Box>
          ))}
        </Paper>
        {subs.map((sq, si) => (
          <Paper key={si} sx={{ p: 1.5, mb: 1.5, border: "1px solid #e0e0e0" }}>
            <Chip label={`${questionNumber}.${si + 1}`} size="small" sx={{ mb: 1 }} />
            {renderLeaf(sq, `${questionNumber}.${si + 1}`)}
          </Paper>
        ))}
      </Box>
    );
  }

  return (
    <Box>
      <QuestionShapeChips question={question} />
      {renderLeaf(question, questionNumber)}
    </Box>
  );
};

// ══════════════════════════════════════════════════════════════════════════
// Component
// ══════════════════════════════════════════════════════════════════════════

const SimilarPaperReview = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // ── list vs review mode — derived from URL so there is no race condition ──
  const jobIdParam = searchParams.get("job");
  const mode = jobIdParam ? "review" : "list";

  // ── list state ──
  const [jobs, setJobs] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(true);
  const [jobsTotal, setJobsTotal] = useState(0);
  const jobsPage = Math.max(parseInt(searchParams.get("page") || "1", 10), 1) - 1;
  const jobsPerPage = Math.max(parseInt(searchParams.get("per_page") || "15", 10), 1);
  const jobsSearch = searchParams.get("search") || "";
  const jobsStatus = searchParams.get("status") || "";
  const jobsSource = searchParams.get("source") || "";
  const jobsDifficulty = searchParams.get("difficulty") || "";
  const jobsVerificationStatus = searchParams.get("verification_status") || "";
  const jobsSubjectId = searchParams.get("subject_id") || "";
  const jobsSchoolId = searchParams.get("school_id") || "";
  const jobsImported = searchParams.get("imported") || "";
  const [jobsSearchInput, setJobsSearchInput] = useState(jobsSearch);
  const [filterOptions, setFilterOptions] = useState({ subjects: [], schools: [], total: 0 });

  // ── review state ──
  const [jobData, setJobData] = useState(null);
  // Plagiarism report for a generated paper (list row or the paper under review)
  const [similarityTarget, setSimilarityTarget] = useState(null);
  const similarityDialog = (
    <SimilarityReportDialog
      open={Boolean(similarityTarget)}
      similarPaperJobId={similarityTarget?.id}
      filename={similarityTarget?.name}
      onClose={() => setSimilarityTarget(null)}
    />
  );
  const [importData, setImportData] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [approvedSet, setApprovedSet] = useState(new Set());
  const [modifiedSet, setModifiedSet] = useState(new Set());
  const [reviewLoading, setReviewLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // ── compare with original ──
  const [compareOpen, setCompareOpen] = useState(false);
  const [compareIndex, setCompareIndex] = useState(0);
  const [originals, setOriginals] = useState(null);
  const [originalsLoading, setOriginalsLoading] = useState(false);

  // ── edit state ──
  // editTarget: null | { type: 'standalone' } | { type: 'stem', parentIndex } | { type: 'subquestion', parentIndex, subIndex }
  const [editMode, setEditMode] = useState(false);
  const [editDraft, setEditDraft] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [editImageUploading, setEditImageUploading] = useState(false);
  const [editImageDropActive, setEditImageDropActive] = useState(false);
  const editFileInputRef = useRef(null);

  // ── generate similar paper — GenerateSimilarPaperDialog owns the run + polling ──
  const [genOpen, setGenOpen] = useState(false);
  const [saveCategoryId, setSaveCategoryId] = useState("");
  const [saveSourceType, setSaveSourceType] = useState("ai_generated");
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const listPollRef = useRef(null);

  // AI diagram (Import Review parity — ai-generate-diagram + upload-image)
  const [diagramDialogOpen, setDiagramDialogOpen] = useState(false);
  const [generatingDiagram, setGeneratingDiagram] = useState(false);
  const [imageUploadingDiagram, setImageUploadingDiagram] = useState(false);
  const [generatedDiagram, setGeneratedDiagram] = useState(null);
  /** @type {{ type: 'stem' | 'sub' | 'standalone'; subIndex?: number } | null} */
  const [diagramTargetPath, setDiagramTargetPath] = useState(null);

  // AI explanation / improve (same import endpoints + compare flow as QuestionBankQuestionEditDialog)
  const [generatingExplanation, setGeneratingExplanation] = useState(false);
  const [healStarting, setHealStarting] = useState(false);
  const [generatingAnswers, setGeneratingAnswers] = useState(false);
  const [improving, setImproving] = useState(false);
  const [aiImproveOpen, setAiImproveOpen] = useState(false);
  const [aiOriginal, setAiOriginal] = useState(null);
  const [aiSuggestion, setAiSuggestion] = useState(null);
  const [aiImprovePath, setAiImprovePath] = useState(null);
  const [aiAccept, setAiAccept] = useState(AI_ACCEPT_DEFAULT);

  // ── per-question regeneration ──
  const [regeneratingIdx, setRegeneratingIdx] = useState(null);
  const [regeneratingSub, setRegeneratingSub] = useState(null);
  const [regeneratingScope, setRegeneratingScope] = useState(null);
  const [redrawingIdx, setRedrawingIdx] = useState(null);

  // ── Create As (Mock Exam / Past Paper / Worksheet) ──
  const [createMenuAnchor, setCreateMenuAnchor] = useState(null);
  const [createTarget, setCreateTarget] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createForm, setCreateForm] = useState({ name: "", format_id: "", school_year_id: "", topic_id: "", price: 0, target_school_ids: [] });
  const [lookupFormats, setLookupFormats] = useState([]);
  const [lookupSchoolYears, setLookupSchoolYears] = useState([]);
  const [lookupTopics, setLookupTopics] = useState([]);
  const [lookupSchools, setLookupSchools] = useState([]);
  const [lookupsLoading, setLookupsLoading] = useState(false);

  // ════════════════════════════════════════════════════════════════════════
  // LIST MODE
  // ════════════════════════════════════════════════════════════════════════

  const fetchJobs = useCallback(async () => {
    setJobsLoading(true);
    try {
      const res = await api.get("/admin/question-bank/similar-paper-jobs", {
        params: {
          page: jobsPage + 1,
          per_page: jobsPerPage,
          search: jobsSearch || undefined,
          status: jobsStatus || undefined,
          source: jobsSource || undefined,
          difficulty: jobsDifficulty || undefined,
          verification_status: jobsVerificationStatus || undefined,
          subject_id: jobsSubjectId || undefined,
          school_id: jobsSchoolId || undefined,
          imported: jobsImported || undefined,
        },
      });
      const data = res.data?.data;
      setJobs(data?.data || []);
      setJobsTotal(data?.total || 0);
    } catch {
      toast.error("Failed to load similar paper jobs");
    } finally {
      setJobsLoading(false);
    }
  }, [jobsDifficulty, jobsImported, jobsPage, jobsPerPage, jobsSearch, jobsSchoolId, jobsSource, jobsStatus, jobsSubjectId, jobsVerificationStatus]);

  useEffect(() => {
    if (mode === "list") fetchJobs();
  }, [mode, fetchJobs]);

  // Only the subjects and schools that actually have generated papers — the full school list runs
  // to hundreds and would bury the twenty that matter here.
  useEffect(() => {
    if (mode !== "list") return;
    let cancelled = false;
    api
      .get("/admin/question-bank/similar-paper-jobs/filter-options")
      .then((res) => {
        if (cancelled) return;
        const data = res.data?.data || {};
        setFilterOptions({
          subjects: Array.isArray(data.subjects) ? data.subjects : [],
          schools: Array.isArray(data.schools) ? data.schools : [],
          total: Number(data.total) || 0,
        });
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [mode]);

  // Auto-refresh list while any jobs are pending/processing
  useEffect(() => {
    const hasActive = jobs.some(j => j.status === "pending" || j.status === "processing");
    if (hasActive && !listPollRef.current) {
      listPollRef.current = setInterval(() => fetchJobs(), 10000);
    } else if (!hasActive && listPollRef.current) {
      clearInterval(listPollRef.current);
      listPollRef.current = null;
    }
    return () => {
      if (listPollRef.current) {
        clearInterval(listPollRef.current);
        listPollRef.current = null;
      }
    };
  }, [jobs, fetchJobs]);

  useEffect(() => {
    setJobsSearchInput(jobsSearch);
  }, [jobsSearch]);

  const updateListParams = (patch) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(patch).forEach(([key, value]) => {
      if (value === "" || value === null || value === undefined) {
        next.delete(key);
      } else {
        next.set(key, String(value));
      }
    });
    if (!next.get("job")) {
      setSearchParams(next);
      return;
    }
    setSearchParams(next);
  };

  // ════════════════════════════════════════════════════════════════════════
  // REVIEW MODE
  // ════════════════════════════════════════════════════════════════════════

  const loadJob = useCallback(async (id) => {
    setReviewLoading(true);
    try {
      const res = await api.get(`/admin/question-bank/similar-paper-job/${id}/preview`);
      const d = res.data?.data;
      // Import metadata (exam_title, source_filename, subject_id, school_name) is kept
      // SEPARATE: spreading the job over it nulled subject_id, which left the worksheet
      // topic picker permanently empty.
      setJobData({ ...(d.import || {}), ...d.job });
      setImportData(d.import || null);
      setSaveSourceType(d.job?.target_source_type || "ai_generated");
      setSaveCategoryId(d.job?.target_category_id || d.import?.target_category_id || "");
      setQuestions(d.parsed_questions || []);
      setCurrentIndex(0);
      setApprovedSet(new Set());
      setModifiedSet(new Set());
      setEditMode(false);
      setEditDraft(null);
      setEditTarget(null);
      setValidationErrors({});
      setOriginals(null);
      setCompareOpen(false);
    } catch {
      toast.error("Failed to load similar paper job");
    } finally {
      setReviewLoading(false);
    }
  }, []);

  useEffect(() => {
    if (mode === "review" && jobIdParam) {
      loadJob(jobIdParam);
    }
  }, [mode, jobIdParam, loadJob]);

  // ── auto-fix (HealSimilarPaperJob): runs on the queue, progress in stats.heal ──
  const healState = jobData?.stats?.heal || null;
  // heal_active comes from the server, which also expires a run whose worker died.
  const healing = jobData?.heal_active ?? ["queued", "running"].includes(healState?.status);

  useEffect(() => {
    if (mode !== "review" || !jobData?.id || !healing) return undefined;
    const timer = setInterval(async () => {
      try {
        const res = await api.get(`/admin/question-bank/similar-paper-job/${jobData.id}/preview`);
        const d = res.data?.data;
        const heal = d?.job?.stats?.heal;
        setJobData((prev) => (prev ? { ...prev, stats: d?.job?.stats, heal_active: d?.job?.heal_active, verification_status: d?.job?.verification_status, verification_report: d?.job?.verification_report } : prev));
        if (!d?.job?.heal_active) {
          setQuestions(d.parsed_questions || []);
          setModifiedSet(new Set());
          if (heal.status === "completed") {
            toast.success(`Auto-fix finished: ${Object.values(heal.summary || {}).join("; ") || "done"}`);
          } else {
            toast.error(`Auto-fix failed${heal.error ? `: ${heal.error}` : ""}`);
          }
        }
      } catch {
        /* keep polling */
      }
    }, 5000);
    return () => clearInterval(timer);
  }, [mode, jobData?.id, healing]);

  /** steps: null = everything (regenerate flagged, redraw figures, fill answers, explanations). */
  const startHeal = async (steps = null) => {
    if (!jobData?.id) return;
    if (editMode) {
      toast.warning("Save or cancel the open edit first");
      return;
    }
    setHealStarting(true);
    try {
      const res = await api.post(`/admin/question-bank/similar-paper-job/${jobData.id}/heal`, {
        ...(steps ? { steps } : {}),
        // Send the unsaved draft so the auto-fix starts from the reviewer's edits.
        ...(modifiedSet.size > 0 ? { questions } : {}),
      });
      const heal = res.data?.data?.heal;
      setJobData((prev) => (prev ? { ...prev, heal_active: true, stats: { ...(prev.stats || {}), heal } } : prev));
      setModifiedSet(new Set());
      toast.info("Auto-fix queued — it runs in the background; you can leave this page.");
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to start auto-fix");
    } finally {
      setHealStarting(false);
    }
  };

  useEffect(() => {
    if (mode !== "review") return undefined;
    const handler = (e) => {
      if (!editMode && modifiedSet.size === 0) return;
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [mode, editMode, modifiedSet]);

  const openReview = (id) => {
    setSearchParams({ job: id });
  };

  const backToList = () => {
    setSearchParams({});
    setJobData(null);
    setQuestions([]);
  };

  // ── navigation ──
  const goTo = (idx) => {
    if (idx >= 0 && idx < questions.length) {
      if (editMode && !window.confirm("Discard the unsaved edit and move on?")) return;
      setCurrentIndex(idx);
      setEditMode(false);
      setEditDraft(null);
      setEditTarget(null);
      setValidationErrors({});
    }
  };

  // ── compare with original ──
  // Originals are fetched once per job and cached: a re-rolled slot keeps its
  // varied_from_question_id, so the map stays valid across Regenerate.
  const openCompare = async (idx) => {
    setCompareIndex(idx);
    setCompareOpen(true);
    if (originals || originalsLoading || !jobData?.id) return;
    setOriginalsLoading(true);
    try {
      const res = await api.get(`/admin/question-bank/similar-paper-job/${jobData.id}/originals`);
      setOriginals(res.data?.data?.originals || {});
    } catch {
      toast.error("Failed to load the original questions");
      setOriginals({});
    } finally {
      setOriginalsLoading(false);
    }
  };

  const goToCompare = (idx) => {
    if (idx >= 0 && idx < questions.length) {
      setCompareIndex(idx);
      goTo(idx);
    }
  };

  // ── approve toggle ──
  const toggleApproval = (idx) => {
    if (!approvedSet.has(idx) && questionNeedsReview(questions[idx])) {
      const reasons = [
        ...(questions[idx]?.type_issues || []),
        ...(questions[idx]?.answer_data?.review_reasons || []),
      ].join(", ");
      if (
        !window.confirm(
          `Question ${idx + 1} is flagged for review${reasons ? ` (${reasons})` : ""}.\n\nApprove it anyway?`
        )
      ) {
        return;
      }
      // The reviewer has just made the call the flag was asking for — drop it so the
      // slot stops reading as unreviewed downstream.
      setQuestions((prev) =>
        prev.map((q, i) => {
          if (i !== idx) return q;
          const { type_issues, ...q2 } = q;
          if (!q.answer_data) return q2;
          const { needs_review, review_reasons, ...rest } = q.answer_data;
          return { ...q2, answer_data: rest };
        })
      );
      setModifiedSet((prev) => new Set(prev).add(idx));
    }
    setApprovedSet((prev) => {
      const next = new Set(prev);
      next.has(idx) ? next.delete(idx) : next.add(idx);
      return next;
    });
  };

  const approveAll = () => {
    setApprovedSet(new Set(questions.map((_, i) => i)));
  };

  const rejectAll = () => setApprovedSet(new Set());

  // ── regenerate a single question slot ──
  // scope: 'slot' re-rolls the whole question, 'passage' replaces a comprehension passage and
  // re-writes its parts against it, 'sub' replaces one part against the passage it already has.
  const handleRegenerate = async (idx, { scope = "slot", subIndex = null } = {}) => {
    if (!jobData?.id) return;
    if (editMode) {
      toast.warning("Save or cancel the open edit first");
      return;
    }
    setRegeneratingIdx(idx);
    setRegeneratingSub(scope === "sub" ? subIndex : null);
    setRegeneratingScope(scope);
    try {
      // The slot is regenerated from what the server holds; send the current draft so
      // an edited stem is what gets re-rolled.
      const res = await api.post(`/admin/question-bank/similar-paper-job/${jobData.id}/regenerate-slot/${idx}`, {
        questions,
        scope,
        ...(scope === "sub" ? { sub_index: subIndex } : {}),
      });
      const nq = res.data?.data?.question;
      if (nq) {
        setQuestions((prev) => prev.map((q, i) => (i === idx ? nq : q)));
        setModifiedSet((prev) => new Set(prev).add(idx));
        setApprovedSet((prev) => {
          const n = new Set(prev);
          n.delete(idx);
          return n;
        });
        toast.success(
          scope === "sub"
            ? `Question ${idx + 1}.${subIndex + 1} regenerated from the passage`
            : scope === "passage"
            ? `Passage and its questions regenerated`
            : res.data?.data?.visual_redrawn
            ? `Question ${idx + 1} regenerated — figure redrawn to match`
            : `Question ${idx + 1} regenerated`
        );
      } else {
        toast.warning("Regeneration returned no question");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Regeneration failed");
    } finally {
      setRegeneratingIdx(null);
      setRegeneratingSub(null);
      setRegeneratingScope(null);
    }
  };

  // ── redraw the figure on a slot (or one sub-question), overriding the reuse of the original ──
  const handleRedrawVisual = async (idx, subIndex = null, sourceQuestions = null) => {
    if (!jobData?.id) return;
    setRedrawingIdx(idx);
    try {
      const res = await api.post(`/admin/question-bank/similar-paper-job/${jobData.id}/redraw-visuals`, {
        indices: [idx],
        ...(subIndex !== null ? { sub_index: subIndex } : {}),
        force: true,
        // setQuestions has not necessarily flushed when this follows a text change, so the caller
        // passes the array it just built rather than letting us read a stale one.
        questions: sourceQuestions || questions,
      });
      const updated = res.data?.data?.questions?.[String(idx)];
      if (!updated) {
        toast.warning("Redraw returned no figure");
        return;
      }
      setQuestions((prev) => prev.map((q, i) => (i === idx ? updated : q)));
      setModifiedSet((prev) => new Set(prev).add(idx));
      toast.success(subIndex !== null ? "Sub-question figure redrawn" : "Figure redrawn");
    } catch (err) {
      toast.error(err.response?.data?.message || "Redraw failed");
    } finally {
      setRedrawingIdx(null);
    }
  };

  // ── Create As (Mock Exam / Past Paper / Worksheet) ──
  const fetchEntityLookups = useCallback(async (target) => {
    setLookupsLoading(true);
    try {
      if (target === "mock_exam" || target === "paper") {
        const [f, y, s] = await Promise.all([
          api.get("common/data?param=Formats"),
          api.get("common/data?param=SchoolYears"),
          api.get("common/data?param=Schools"),
        ]);
        setLookupFormats(f.data?.data || f.data || []);
        setLookupSchoolYears(y.data?.data || y.data || []);
        const raw = s.data?.data;
        setLookupSchools(Array.isArray(raw) ? raw : []);
      } else if (target === "worksheet") {
        const subjectId = importData?.subject_id ?? jobData?.subject_id;
        if (subjectId) {
          const t = await api.get("/admin/question-bank/topics-by-subject", { params: { subject_id: subjectId } });
          setLookupTopics(t.data?.data || []);
        } else {
          setLookupTopics([]);
        }
      }
    } catch {
      toast.error("Failed to load options");
    } finally {
      setLookupsLoading(false);
    }
  }, [importData, jobData]);

  const openCreateDialog = (target) => {
    setCreateMenuAnchor(null);
    setCreateTarget(target);
    const defName =
      target === "mock_exam"
        ? standardMockExamName(importData, jobData)
        : jobData?.exam_title || jobData?.source_filename || `Similar Paper #${jobData?.id}`;
    const importSchoolId = importData?.school_id ? Number(importData.school_id) : null;
    setCreateForm({
      name: defName,
      format_id: "",
      school_year_id: "",
      topic_id: "",
      price: 0,
      target_school_ids: importSchoolId ? [importSchoolId] : [],
    });
    setCreateOpen(true);
    fetchEntityLookups(target);
  };

  const handleCreateEntity = async () => {
    if (!jobData?.id || !createTarget) return;
    if (!validateDestinationForm(createTarget, createForm)) return;

    const approvedIndices = [...approvedSet].sort((a, b) => a - b);
    if (approvedIndices.length === 0) {
      toast.warning("Approve at least one question first");
      return;
    }

    setCreating(true);
    try {
      // Persist edits, then push ONLY the approved questions into the bank, so the
      // created entity is built from exactly what was reviewed.
      await persistQuestions(questions);
      await api.post(`/admin/question-bank/similar-paper-job/${jobData.id}/confirm`, {
        approved_indices: approvedIndices,
        questions,
        target_source_type: saveSourceType,
        target_category_id: saveCategoryId || undefined,
      });

      const payload = {
        target: createTarget,
        name: createForm.name.trim(),
        category_id: saveCategoryId || undefined,
      };
      if (createTarget === "mock_exam" || createTarget === "paper") {
        payload.format_id = createForm.format_id;
        payload.school_year_id = createForm.school_year_id;
        payload.price = createForm.price || 0;
        payload.target_school_ids = (createForm.target_school_ids || []).map(Number).filter(Boolean);
        if (payload.target_school_ids[0]) payload.school_id = payload.target_school_ids[0];
      }
      if (createTarget === "worksheet") payload.topic_id = createForm.topic_id;

      const res = await api.post(`/admin/question-bank/similar-paper-job/${jobData.id}/create-entity`, payload);
      const d = res.data?.data;
      toast.success(res.data?.message || "Created successfully");
      setCreateOpen(false);
      if (d?.edit_path) navigate(d.edit_path);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to create");
    } finally {
      setCreating(false);
    }
  };

  const CREATE_TARGET_LABELS = { mock_exam: "Mock Exam", paper: "Past Paper", worksheet: "Worksheet" };
  const CREATE_TARGET_ROUTES = { mock_exam: "/admin/mock-exams", paper: "/admin/papers", worksheet: "/admin/test-list" };

  // ── edit ──
  const startEditStandalone = () => {
    const q = questions[currentIndex];
    if (!q) return;
    setValidationErrors({});
    setEditDraft(hydrateDraftForEdit(q));
    setEditTarget({ type: "standalone" });
    setEditMode(true);
  };

  const startEditStem = (parentIndex) => {
    const p = questions[parentIndex];
    if (!p) return;
    setValidationErrors({});
    setEditDraft({
      type: "grouped",
      question_text: p.question_text,
      default_marks: p.default_marks || 1,
      difficulty: normalizeDifficulty(p.difficulty),
      topic: p.topic ?? "",
      passage_type: p.passage_type ?? null,
      section_title: p.section_title ?? null,
      section_instructions: p.section_instructions ?? null,
      image_url: p.image_url ?? null,
      image_urls: p.image_urls ?? null,
      image_description: p.image_description ?? null,
      // Without these the stem editor cannot tell an inherited source-paper figure from one
      // drawn for this slot, and the reuse plan would silently outlive a removed image.
      has_original_image: p.has_original_image ?? null,
      original_image_url: p.original_image_url ?? null,
      freeze_visual: p.freeze_visual ?? null,
    });
    setEditTarget({ type: "stem", parentIndex });
    setEditMode(true);
  };

  const startEditSubQuestion = (parentIndex, subIndex) => {
    const sq = questions[parentIndex]?.sub_questions?.[subIndex];
    if (!sq) return;
    setValidationErrors({});
    setEditDraft(hydrateDraftForEdit(sq));
    setEditTarget({ type: "subquestion", parentIndex, subIndex });
    setEditMode(true);
  };

  const startEdit = () => {
    const q = questions[currentIndex];
    // A grouped slot with no parts is a question the reviewer must be able to retype —
    // Edit Stem hid the type selector and left no way to change or delete it.
    if (q?.type === "grouped" && (q.sub_questions || []).length > 0) {
      startEditStem(currentIndex);
      return;
    }
    startEditStandalone();
  };

  const deleteQuestionAt = async (idx) => {
    if (!window.confirm("Remove this question from the paper?")) return;
    const next = questions.filter((_, i) => i !== idx);
    if (next.length === 0) {
      toast.error("The paper must have at least one question");
      return;
    }
    const shiftSet = (prev) => {
      const n = new Set();
      prev.forEach((i) => {
        if (i < idx) n.add(i);
        else if (i > idx) n.add(i - 1);
      });
      return n;
    };
    setQuestions(next);
    setApprovedSet(shiftSet);
    setModifiedSet(shiftSet);
    setCurrentIndex(Math.min(idx, next.length - 1));
    setEditMode(false);
    setEditDraft(null);
    setEditTarget(null);
    try {
      await persistQuestions(next);
      setModifiedSet(new Set());
      toast.success("Question removed");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Could not remove the question");
    }
  };

  const deleteCurrentQuestion = async () => {
    await deleteQuestionAt(currentIndex);
  };

  const addStandaloneQuestion = () => {
    const newQ = {
      type: "short-answer",
      question_text: "",
      difficulty: "Medium",
      default_marks: 1,
      topic: "",
      answer_data: { correct_answer_text: "" },
      options: [],
      sub_questions: [],
      explanation: "",
    };
    const insertAt = currentIndex + 1;
    const next = [...questions.slice(0, insertAt), newQ, ...questions.slice(insertAt)];
    setQuestions(next);
    setCurrentIndex(insertAt);
    setModifiedSet((prev) => {
      const n = new Set();
      prev.forEach((i) => n.add(i >= insertAt ? i + 1 : i));
      n.add(insertAt);
      return n;
    });
    setApprovedSet((prev) => {
      const n = new Set();
      prev.forEach((i) => n.add(i >= insertAt ? i + 1 : i));
      return n;
    });
    setValidationErrors({});
    setEditDraft(hydrateDraftForEdit(newQ));
    setEditTarget({ type: "standalone" });
    setEditMode(true);
  };

  const addSubQuestionAt = (parentIndex) => {
    const parent = questions[parentIndex];
    if (!parent) return;
    const newSub = emptySubQuestion(parent.difficulty);
    const newSubIndex = (parent.sub_questions || []).length;
    setQuestions((prev) => {
      const next = [...prev];
      next[parentIndex] = {
        ...next[parentIndex],
        type: "grouped",
        sub_questions: [...(next[parentIndex].sub_questions || []), newSub],
      };
      return next;
    });
    setModifiedSet((prev) => new Set(prev).add(parentIndex));
    setValidationErrors({});
    setEditDraft(JSON.parse(JSON.stringify(newSub)));
    setEditTarget({ type: "subquestion", parentIndex, subIndex: newSubIndex });
    setEditMode(true);
  };

  const addSubQuestionToCurrentQuestion = () => {
    const q = questions[currentIndex];
    if (!q) return;
    if (q.type === "grouped") {
      addSubQuestionAt(currentIndex);
      return;
    }
    const newSub = emptySubQuestion(q.difficulty);
    setQuestions((prev) => {
      const next = [...prev];
      next[currentIndex] = {
        ...next[currentIndex],
        type: "grouped",
        sub_questions: [newSub],
      };
      return next;
    });
    setModifiedSet((prev) => new Set(prev).add(currentIndex));
    setValidationErrors({});
    setEditDraft(JSON.parse(JSON.stringify(newSub)));
    setEditTarget({ type: "subquestion", parentIndex: currentIndex, subIndex: 0 });
    setEditMode(true);
  };

  const deleteSubQuestion = async (parentIndex, subIndex) => {
    if (!window.confirm("Remove this sub-question?")) return;
    const next = [...questions];
    const subs = [...(next[parentIndex].sub_questions || [])];
    subs.splice(subIndex, 1);
    next[parentIndex] = { ...next[parentIndex], sub_questions: subs };
    setQuestions(next);
    setModifiedSet((prev) => new Set(prev).add(parentIndex));
    try {
      await persistQuestions(next);
      setModifiedSet(new Set());
    } catch (err) {
      toast.error(err.response?.data?.message || "Removed locally — use Save Draft to retry");
    }
    if (editTarget?.type === "subquestion" && editTarget.parentIndex === parentIndex) {
      setEditMode(false);
      setEditDraft(null);
      setEditTarget(null);
      setValidationErrors({});
    }
  };

  const cancelEdit = () => {
    setEditMode(false);
    setEditDraft(null);
    setEditTarget(null);
    setValidationErrors({});
  };

  const saveEdit = async () => {
    if (!editDraft || savingEdit) return;
    let draftToSave = { ...editDraft };
    if (["fill-in-the-blank", "multi-fill"].includes(draftToSave.type)) {
      const blanksLen = draftToSave.answer_data?.correct_blanks?.length
        ?? (Array.isArray(draftToSave.blanks) ? draftToSave.blanks.length : 0);
      draftToSave.type = blanksLen >= 2 ? "multi-fill" : "fill-in-the-blank";
      const first = draftToSave.answer_data?.correct_blanks?.[0];
      const firstText = Array.isArray(first) ? first[0] : first;
      draftToSave = {
        ...draftToSave,
        answer_data: {
          ...(draftToSave.answer_data || {}),
          correct_answer_text: firstText || draftToSave.answer_data?.correct_answer_text || "",
        },
      };
    }
    if (
      ["multiple-choice", "multiple-select", "true-false"].includes(draftToSave.type) &&
      Array.isArray(draftToSave.options)
    ) {
      const correctIndices = draftToSave.options
        .map((opt, i) => (opt.is_correct ? i : -1))
        .filter((i) => i >= 0);
      draftToSave = {
        ...draftToSave,
        answer_data: {
          ...(draftToSave.answer_data || {}),
          correct_options: correctIndices,
        },
      };
    }
    if (editTarget?.type !== "stem") {
      const validation = validateQuestion(draftToSave);
      if (!validation.valid) {
        setValidationErrors(validation.errors);
        toast.error("Please fix validation errors before saving");
        return;
      }
    }
    setValidationErrors({});
    const updated = [...questions];
    if (editTarget?.type === "subquestion") {
      const { parentIndex, subIndex } = editTarget;
      const subQuestions = [...(updated[parentIndex].sub_questions || [])];
      subQuestions[subIndex] = { ...draftToSave };
      updated[parentIndex] = { ...updated[parentIndex], sub_questions: subQuestions };
      setModifiedSet((prev) => new Set(prev).add(parentIndex));
    } else if (editTarget?.type === "stem") {
      const { parentIndex } = editTarget;
      updated[parentIndex] = {
        ...updated[parentIndex],
        question_text: draftToSave.question_text,
        default_marks: draftToSave.default_marks,
        difficulty: draftToSave.difficulty,
        topic: draftToSave.topic,
        passage_type: draftToSave.passage_type ?? null,
        section_title: draftToSave.section_title ?? null,
        section_instructions: draftToSave.section_instructions ?? null,
        image_url: draftToSave.image_url ?? null,
        image_urls: draftToSave.image_urls ?? null,
        image_description: draftToSave.image_description ?? null,
        // The stem editor exposes an explanation field, but it was absent from this whitelist, so the
        // edit was dropped on save while the toast still said it succeeded.
        explanation: draftToSave.explanation ?? updated[parentIndex]?.explanation ?? null,
        has_original_image: draftToSave.has_original_image ?? null,
        original_image_url: draftToSave.original_image_url ?? null,
        freeze_visual: draftToSave.freeze_visual ?? null,
      };
      setModifiedSet((prev) => new Set(prev).add(parentIndex));
    } else {
      updated[currentIndex] = { ...draftToSave };
      setModifiedSet((prev) => new Set(prev).add(currentIndex));
    }
    setQuestions(updated);
    setEditMode(false);
    setEditDraft(null);
    setEditTarget(null);

    // Persist immediately: the local-only save left every edit at the mercy of a
    // reload, a Regenerate (which re-reads the stored draft) or an accidental back.
    setSavingEdit(true);
    try {
      await persistQuestions(updated);
      setModifiedSet(new Set());
      toast.success("Question saved");
    } catch (err) {
      toast.error(
        err.response?.data?.message || "Saved locally, but the server rejected it — use Save Draft to retry"
      );
    } finally {
      setSavingEdit(false);
    }
  };

  const updateDraftField = (field, value) => {
    setEditDraft((prev) => ({ ...prev, [field]: value }));
  };

  const patchAnswerData = (patch) => {
    setEditDraft((prev) => ({
      ...prev,
      answer_data: { ...(prev.answer_data || {}), ...patch },
    }));
  };

  const applyTypeChange = (newType) => {
    setEditDraft((prev) => ({
      ...prev,
      ...defaultsForType(newType),
      type: newType,
    }));
    setValidationErrors({});
  };

  const updateFillMarksConfig = (mode, fixedTotal) => {
    setEditDraft((prev) => {
      const ad = { ...(prev.answer_data || {}) };
      if (mode === "fixed") {
        const total = parseInt(fixedTotal, 10) || 1;
        ad.marks_mode = "fixed";
        ad.marks_total = total;
        return { ...prev, default_marks: total, answer_data: ad };
      }
      delete ad.marks_mode;
      delete ad.marks_total;
      return { ...prev, answer_data: ad };
    });
  };

  const uploadEditStemImage = async (file) => {
    if (!file || !String(file.type || "").startsWith("image/")) {
      toast.error("Please choose an image file.");
      return;
    }
    setEditImageUploading(true);
    try {
      const formData = new FormData();
      formData.append("image", file);
      const res = await api.post("/admin/question-bank/upload-image", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const newUrl = res.data?.data?.image_url || "";
      if (newUrl) {
        setEditDraft((prev) => {
          const urls = [...collectImageUrls(prev), newUrl];
          return { ...prev, ...syncDraftImageFields(urls), ...clearedOriginalFigureFlags(prev) };
        });
        toast.success("Image uploaded");
      }
    } catch {
      toast.error("Image upload failed");
    } finally {
      setEditImageUploading(false);
      if (editFileInputRef.current) editFileInputRef.current.value = "";
    }
  };

  const removeEditStemImage = async (urlToRemove) => {
    // A slot can inherit the source paper's figure verbatim (planVisualForOne reuse branches),
    // in which case the file is the original past paper's — drop the reference only.
    const inherited = usesOriginalFigure(editDraft) || editDraft?.original_image_url === urlToRemove;
    setEditDraft((prev) => {
      const urls = collectImageUrls(prev).filter((u) => u !== urlToRemove);
      return { ...prev, ...syncDraftImageFields(urls), ...clearedOriginalFigureFlags(prev) };
    });
    if (inherited) {
      toast.success("Figure removed — the source paper keeps its image");
      return;
    }
    try {
      await api.post("/admin/question-bank/delete-image", {
        url: urlToRemove,
        similar_paper_job_id: jobData?.id,
      });
    } catch {
      setEditDraft((prev) => {
        const urls = [...collectImageUrls(prev), urlToRemove];
        return { ...prev, ...syncDraftImageFields(urls) };
      });
      toast.error("Failed to remove image");
    }
  };

  // ── save draft to server ──
  const saveDraft = async () => {
    if (!jobData || savingDraft) return;
    // `questions` does not contain the open edit panel's draft, so saving here would
    // quietly write the pre-edit question back over it.
    if (editMode) {
      toast.warning("Save or cancel the open edit first");
      return;
    }
    setSavingDraft(true);
    try {
      await api.post(`/admin/question-bank/similar-paper-job/${jobData.id}/update-questions`, {
        generated_questions: questions,
      });
      setModifiedSet(new Set());
      toast.success("Draft saved");
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to save draft");
    } finally {
      setSavingDraft(false);
    }
  };

  const persistQuestions = async (nextQuestions) => {
    if (!jobData) return;
    await api.post(`/admin/question-bank/similar-paper-job/${jobData.id}/update-questions`, {
      generated_questions: nextQuestions,
    });
  };

  const mergeGeneratedExplanation = (prev, text) => {
    const formatted = formatAiExplanation(text);
    const hasRichMedia = /<img\s/i.test(prev || "");
    if (hasRichMedia && String(prev || "").replace(/<[^>]*>/g, "").trim()) {
      return `${prev}<p><br></p>${formatted}`;
    }
    return formatted;
  };

  const resolveQuestionAtPath = (path) => {
    if (!path) return null;
    if (path.type === "sub") {
      return questions[path.parentIndex ?? currentIndex]?.sub_questions?.[path.subIndex];
    }
    return questions[path.index ?? currentIndex];
  };

  const handleGenerateExplanation = async (path) => {
    const importId = jobData?.import_id;
    if (!importId) {
      toast.error("No source import linked to this job");
      return;
    }
    const q = path?.fromEdit ? editDraft : resolveQuestionAtPath(path);
    if (!q) {
      toast.error("Question not found");
      return;
    }
    setGeneratingExplanation(true);
    try {
      const res = await api.post(`/admin/question-bank/import/${importId}/generate-explanation`, {
        question: q,
      });
      const text = res.data?.data?.explanation;
      if (!text) {
        toast.warning("No explanation returned");
        return;
      }
      const merged = mergeGeneratedExplanation(q.explanation, text);
      if (path?.fromEdit) {
        updateDraftField("explanation", merged);
      } else {
        const next = structuredClone(questions);
        if (path.type === "sub") {
          next[path.parentIndex].sub_questions[path.subIndex].explanation = merged;
        } else {
          next[path.index].explanation = merged;
        }
        setQuestions(next);
        setModifiedSet((prev) => new Set(prev).add(path.type === "sub" ? path.parentIndex : path.index));
        try {
          await persistQuestions(next);
          setModifiedSet(new Set());
        } catch {
          toast.warning("Explanation added locally — click Save Draft to store it");
        }
      }
      toast.success("Explanation generated in step-wise format");
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to generate explanation");
    } finally {
      setGeneratingExplanation(false);
    }
  };

  const handleGenerateAnswers = async (indices = null) => {
    if (!jobData?.id) return;
    if (!questions.length) {
      toast.warning("No questions to answer");
      return;
    }
    if (editMode) {
      toast.warning("Save or cancel the open edit first");
      return;
    }
    setGeneratingAnswers(true);
    try {
      await persistQuestions(questions);
      const payload = {
        questions,
        ...(indices ? { question_indices: indices } : {}),
      };
      const res = await api.post(
        `/admin/question-bank/similar-paper-job/${jobData.id}/generate-answers`,
        payload
      );
      const data = res.data?.data;
      if (data?.parsed_questions) {
        setQuestions(data.parsed_questions);
        if (indices?.length) {
          setModifiedSet((prev) => {
            const next = new Set(prev);
            indices.forEach((i) => next.add(i));
            return next;
          });
        } else {
          setModifiedSet(new Set(data.parsed_questions.map((_, i) => i)));
        }
        toast.success(
          `Answers generated: ${data.answers_generated}/${data.total_questions} questions now have answers`
        );
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to generate answers");
    } finally {
      setGeneratingAnswers(false);
    }
  };

  const handleAiImprove = async (path) => {
    const importId = jobData?.import_id;
    if (!importId) {
      toast.error("No source import linked to this job");
      return;
    }
    const q = resolveQuestionAtPath(path);
    if (!q) {
      toast.error("Question not found");
      return;
    }
    setImproving(true);
    try {
      const res = await api.post(`/admin/question-bank/import/${importId}/ai-improve-question`, {
        question: q,
      });
      setAiOriginal(q);
      setAiSuggestion(withFormattedExplanation(res.data?.data?.improved_question));
      setAiImprovePath(path);
      setAiAccept(AI_ACCEPT_DEFAULT);
      setAiImproveOpen(true);
    } catch (err) {
      toast.error(err.response?.data?.message || "AI improvement failed. Please try again.");
    } finally {
      setImproving(false);
    }
  };

  const applyAiSuggestion = async () => {
    if (!aiSuggestion || !aiImprovePath) return;
    const merged = mergeAiSuggestion(aiOriginal, aiSuggestion, aiAccept);
    const next = structuredClone(questions);
    if (aiImprovePath.type === "sub") {
      const existing = next[aiImprovePath.parentIndex].sub_questions[aiImprovePath.subIndex] || {};
      next[aiImprovePath.parentIndex].sub_questions[aiImprovePath.subIndex] = {
        ...existing,
        ...merged,
      };
    } else {
      const existing = next[aiImprovePath.index] || {};
      next[aiImprovePath.index] = { ...existing, ...merged };
    }
    setQuestions(next);
    setModifiedSet((prev) =>
      new Set(prev).add(aiImprovePath.type === "sub" ? aiImprovePath.parentIndex : aiImprovePath.index)
    );
    setEditMode(false);
    setEditDraft(null);
    setEditTarget(null);
    setValidationErrors({});
    setAiImproveOpen(false);
    try {
      await persistQuestions(next);
      setModifiedSet(new Set());
      toast.success("Applied AI suggestion");
    } catch {
      toast.warning("Applied locally — click Save Draft to store it");
    }

    // The stem the figure was drawn for has just changed, so redraw it to match.
    const parentIdx = aiImprovePath.type === "sub" ? aiImprovePath.parentIndex : aiImprovePath.index;
    const touched = aiImprovePath.type === "sub"
      ? next[parentIdx]?.sub_questions?.[aiImprovePath.subIndex]
      : next[parentIdx];
    if (slotHasFigure(touched)) {
      await handleRedrawVisual(parentIdx, aiImprovePath.type === "sub" ? aiImprovePath.subIndex : null, next);
    }
  };

  const handleAiGenerateDiagram = async (target) => {
    let q;
    if (target.type === "sub") {
      q = questions[currentIndex]?.sub_questions?.[target.subIndex];
    } else {
      q = questions[currentIndex];
    }
    if (!q) {
      toast.error("Question not found");
      return;
    }
    setGeneratingDiagram(true);
    setGeneratedDiagram(null);
    try {
      const optionTexts = (q.options || [])
        .map((o) => String(o.text || o.option_text || "").trim())
        .filter(Boolean);
      const correctOption = (q.options || []).find((o) => o.is_correct);
      // The spec route: the model returns a structured visual, MathsVisualGenerator draws it and a
      // vision pass checks it against the stem — the legacy import route just asked for raw SVG.
      const res = await api.post("/admin/question-bank/generate-diagram", {
        image_description: q.image_description || q.diagram_description || "",
        question_text: stripHtml(q.question_text),
        subject_id: importData?.subject_id ?? jobData?.subject_id ?? null,
        visual: q.visual_data || null,
        answer:
          String(correctOption?.text || correctOption?.option_text || "").trim()
          || String(q.answer_data?.correct_answer_text || "").trim()
          || null,
        options: optionTexts.length >= 2 ? optionTexts : null,
        include_option_images: false,
      });
      const data = res.data?.data || {};
      if (!data.image_url && !data.svg) {
        toast.error("Diagram generation returned nothing");
        return;
      }
      setGeneratedDiagram(data);
      setDiagramTargetPath(target);
      setDiagramDialogOpen(true);
      if (data.verified === false) {
        toast.warning("Diagram generated, but it may not match the question — check it before adding.");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "AI diagram generation failed. Please try again.");
    } finally {
      setGeneratingDiagram(false);
    }
  };

  const applyGeneratedDiagram = async () => {
    if (!diagramTargetPath) return;

    setImageUploadingDiagram(true);
    try {
      // The spec route stores the figure itself and hands back its URL; only the legacy raw-SVG
      // reply still needs uploading.
      let imageUrl = generatedDiagram?.image_url || null;
      if (!imageUrl && generatedDiagram?.svg && jobData?.import_id) {
        const svgBlob = new Blob([generatedDiagram.svg], { type: "image/svg+xml" });
        const svgFile = new File([svgBlob], `diagram_${Date.now()}.svg`, { type: "image/svg+xml" });
        const formData = new FormData();
        formData.append("image", svgFile);
        const res = await api.post(
          `/admin/question-bank/import/${jobData.import_id}/upload-image`,
          formData,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
        imageUrl = res.data?.data?.image_url || null;
      }

      if (!imageUrl) {
        toast.error("Diagram upload failed — no URL returned");
        return;
      }

      const diagType = generatedDiagram?.diagram_type ?? null;
      const hubVal = generatedDiagram?.hub_value ?? null;
      const visualSpec = generatedDiagram?.visual ?? null;

      const next = structuredClone(questions);
      const idx = currentIndex;
      const applyToQuestion = (qq) => {
        qq.image_url = imageUrl;
        qq.image_urls = [imageUrl];
        // The PDF compositor reads generated_svg first, then render_image_urls, and only then
        // image_url — so a figure drawn here is invisible in the PDF until the two it prefers are
        // brought in line with it.
        delete qq.generated_svg;
        qq.render_image_urls = [imageUrl];
        // This figure is no longer the source paper's, and nothing should treat it as frozen.
        delete qq.has_original_image;
        delete qq.freeze_visual;
        delete qq.original_image_url;
        // Keeping the spec means a later regen-images run redraws this figure deterministically
        // instead of falling back to a freehand raster.
        if (visualSpec) {
          qq.visual_data = visualSpec;
          qq.image_type = "svg";
          qq.visual_status = null;
        }
        if (generatedDiagram?.image_description && !qq.image_description) {
          qq.image_description = generatedDiagram.image_description;
        }
        if (diagType) {
          qq.answer_data = {
            ...(qq.answer_data || {}),
            diagram_type: diagType,
            hub_value: hubVal,
          };
        }
      };

      if (diagramTargetPath.type === "stem") {
        applyToQuestion(next[idx]);
      } else if (diagramTargetPath.type === "sub") {
        applyToQuestion(next[idx].sub_questions[diagramTargetPath.subIndex]);
      } else {
        applyToQuestion(next[idx]);
      }

      setQuestions(next);
      setModifiedSet((prev) => new Set(prev).add(idx));
      await persistQuestions(next);

      setDiagramDialogOpen(false);
      setGeneratedDiagram(null);
      setDiagramTargetPath(null);
      toast.success("Diagram added to question");
    } catch {
      toast.error("Failed to upload diagram. Please try again.");
    } finally {
      setImageUploadingDiagram(false);
    }
  };

  // ── confirm import ──
  const handleConfirm = async () => {
    const approvedIndices = [...approvedSet].sort((a, b) => a - b);
    if (approvedIndices.length === 0) {
      toast.warning("No questions approved");
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post(`/admin/question-bank/similar-paper-job/${jobData.id}/confirm`, {
        approved_indices: approvedIndices,
        questions,
        target_source_type: saveSourceType,
        target_category_id: saveCategoryId || undefined,
      });
      const d = res.data?.data;
      if (d?.already_imported) {
        // A re-confirm pushes the edits onto the rows already in the bank, which is what the mock
        // exam / paper built from this job serves.
        toast.success(res.data?.message || `${d.questions_updated ?? 0} questions updated in the Question Bank`);
        await loadJob(jobData.id);
      } else {
        toast.success(`${d.questions_imported} questions imported to Question Bank`);
      }
      setSaveDialogOpen(false);
      if (!d?.already_imported) backToList();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to import questions");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteGenerated = async (payload) => {
    if (!jobData?.id) return;
    setDeleting(true);
    try {
      const res = await api.delete(
        `/admin/question-bank/similar-paper-job/${jobData.id}/generated`,
        { data: payload }
      );
      toast.success(res.data?.message || "Generated questions deleted");
      setDeleteOpen(false);
      if (res.data?.data?.job_deleted) {
        if (mode === "review") backToList();
        else await fetchJobs();
        return;
      }
      if (mode === "review") await loadJob(jobData.id);
      else await fetchJobs();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to delete generated questions");
    } finally {
      setDeleting(false);
    }
  };

  const handleDownloadBackendPdf = async () => {
    const importId = jobData?.import_id;
    let pdfUrl = null;

    if (jobData?.download_filename && importId) {
      pdfUrl = `/admin/question-bank/import/${importId}/similar-paper/${jobData.download_filename}`;
    }

    // Older jobs may not have stored a filename — ask the status endpoint.
    if (!pdfUrl && importId) {
      try {
        const statusRes = await api.get(`/admin/question-bank/import/${importId}/similar-paper-status`, {
          params: jobData?.id ? { job_id: jobData.id } : {},
        });
        const status = statusRes.data?.data;
        if (status?.filename) {
          pdfUrl = `/admin/question-bank/import/${importId}/similar-paper/${status.filename}`;
        }
      } catch {
        // ignore
      }
    }

    if (!pdfUrl) {
      toast.error("PDF not found. Please generate a new paper.");
      return;
    }

    setDownloadingPdf(true);
    try {
      const res = await api.get(pdfUrl, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = "similar-paper.pdf";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(objectUrl);
      toast.success("PDF downloaded");
    } catch (err) {
      console.error("Download error:", err);
      toast.error("Download failed. Please try again.");
    } finally {
      setDownloadingPdf(false);
    }
  };

  // ── save similar paper questions to question bank ──
  const handleDownloadPdf = (onlyApproved = false) => {
    const targetQuestions = onlyApproved
      ? questions.filter((_, i) => approvedSet.has(i))
      : questions;

    if (targetQuestions.length === 0) {
      toast.warning(onlyApproved ? "No approved questions to export" : "No questions to export");
      return;
    }

    const optionLabel = (idx) => String.fromCharCode(65 + idx);

    // The print window is a bare document, so a stored /storage/... or bare
    // question-bank path resolves against the wrong origin unless we do what the
    // on-screen renderer already does.
    const renderImages = (q, alt) => {
      const urls = q.image_urls?.length ? q.image_urls : q.image_url ? [q.image_url] : [];
      if (!urls.length) return "";
      const tags = urls
        .map((u) => resolveQuestionHtmlAssetUrl(u))
        .filter(Boolean)
        .map((u) => `<img src="${u}" alt="${alt}" />`)
        .join("");
      return tags ? `<div class="q-images">${tags}</div>` : "";
    };

    const renderAnswerHtml = (q) => {
      const ad = q.answer_data || {};
      const opts = q.options || [];

      if (["multiple-choice", "multiple-select", "true-false"].includes(q.type) && opts.length > 0) {
        const rows = opts
          .map((opt, i) => {
            const text = opt.text || opt.option_text || "";
            const correct = opt.is_correct;
            return `<div class="option${correct ? " correct" : ""}">
              <span class="opt-label">${optionLabel(i)}.</span> ${text}${correct ? " <span class='tick'>✓</span>" : ""}
            </div>`;
          })
          .join("");
        return `<div class="q-options">${rows}</div>`;
      }

      if (["fill-in-the-blank", "short-answer"].includes(q.type) && ad.correct_answer_text) {
        return `<div class="q-answer"><strong>Answer:</strong> ${ad.correct_answer_text}</div>`;
      }

      if (q.type === "essay" && ad.model_answer) {
        return `<div class="q-answer"><strong>Model Answer:</strong> ${ad.model_answer}</div>`;
      }

      if (q.type === "multi-fill" && Array.isArray(ad.correct_blanks)) {
        const labels = ad.blank_labels || [];
        const rows = ad.correct_blanks
          .map((blank, i) => {
            const ans = Array.isArray(blank) ? blank[0] : blank;
            const alts = Array.isArray(blank) && blank.length > 1 ? ` (also: ${blank.slice(1).join(", ")})` : "";
            const label = labels[i] ? `<strong>${labels[i]}</strong>` : `Blank ${i + 1}`;
            return `<div class="blank-row">${label}: <span class="correct-ans">${ans}</span>${alts}</div>`;
          })
          .join("");
        return `<div class="q-answer">${rows}</div>`;
      }

      if (q.type === "matching" && opts.length > 0) {
        const rows = opts
          .map((opt) => `<div class="match-row">${opt.text || opt.option_text || ""} → <span class="correct-ans">${opt.match_text || ""}</span></div>`)
          .join("");
        return `<div class="q-answer"><strong>Matching Pairs:</strong>${rows}</div>`;
      }

      if (q.type === "ordering" && opts.length > 0) {
        const rows = opts
          .map((opt, i) => `<div class="order-row"><strong>${i + 1}.</strong> ${opt.text || opt.option_text || ""}</div>`)
          .join("");
        return `<div class="q-answer"><strong>Correct Order:</strong>${rows}</div>`;
      }

      return "";
    };

    const renderQuestionBlock = (q, num) => {
      const typeLabel = q.type || "";
      const diffLabel = q.difficulty || "";
      const marks = q.default_marks ? `${q.default_marks}m` : "";
      const subtopic = q.topic ? `<span class="badge badge-sub">${q.topic}</span>` : "";
      const images = renderImages(q, "question image");

      return `<div class="question">
        <div class="q-header">
          <span class="q-num">Q${num}</span>
          ${typeLabel ? `<span class="badge badge-type">${typeLabel}</span>` : ""}
          ${diffLabel ? `<span class="badge badge-diff">${diffLabel}</span>` : ""}
          ${marks ? `<span class="badge badge-marks">${marks}</span>` : ""}
          ${subtopic}
        </div>
        <div class="q-text">${q.question_text || "<em>(no text)</em>"}</div>
        ${images}
        ${renderAnswerHtml(q)}
      </div>`;
    };

    let bodyHtml = "";
    targetQuestions.forEach((q, localIdx) => {
      const origIdx = onlyApproved
        ? questions.indexOf(q)
        : localIdx;
      const num = origIdx + 1;

      if (q.type === "grouped") {
        const subImages = renderImages(q, "stem image");
        bodyHtml += `<div class="question grouped">
          <div class="q-header">
            <span class="q-num">Q${num}</span>
            <span class="badge badge-type">grouped</span>
          </div>
          <div class="q-text q-stem"><strong>Shared Stem:</strong><br>${q.question_text || ""}</div>
          ${subImages}
          <div class="sub-questions">`;
        (q.sub_questions || []).forEach((sq, si) => {
          bodyHtml += renderQuestionBlock(sq, `${num}.${si + 1}`);
        });
        bodyHtml += `</div></div>`;
      } else {
        bodyHtml += renderQuestionBlock(q, num);
      }
    });

    const title = jobData?.exam_title || jobData?.source_filename || `Similar Paper Job #${jobData?.id}`;
    const subtitle = [
      jobData?.exam_subject,
      jobData?.school_name,
      `Difficulty: ${jobData?.difficulty || "Mixed"}`,
      `Source: ${jobData?.source || "AI"}`,
      `${targetQuestions.length} question${targetQuestions.length !== 1 ? "s" : ""}`,
    ]
      .filter(Boolean)
      .join(" · ");

    const pageHtml = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <title>${title}</title>
  <script>
    // The print window is a blank document, so it needs its own MathJax. Print from
    // inside it once typesetting has settled — a fixed delay would catch half-laid-out
    // equations. The opener still prints as a fallback if the CDN is unreachable.
    window.MathJax = {
      tex: { inlineMath: [['$', '$'], ['\\\\(', '\\\\)']], displayMath: [['$$', '$$'], ['\\\\[', '\\\\]']] },
      startup: {
        pageReady() {
          return MathJax.startup.defaultPageReady().then(function () {
            window.__printed = true;
            setTimeout(function () { window.print(); }, 150);
          });
        }
      }
    };
  <\u002Fscript>
  <script src="https://cdn.jsdelivr.net/npm/mathjax@3/es5/tex-chtml.js"><\u002Fscript>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: Arial, Helvetica, sans-serif; font-size: 11pt; color: #111; padding: 18mm 20mm; }
    h1 { font-size: 15pt; margin-bottom: 4px; }
    .subtitle { font-size: 9pt; color: #555; margin-bottom: 12px; }
    hr { border: none; border-top: 1px solid #bbb; margin-bottom: 16px; }
    .question { margin-bottom: 18px; padding-bottom: 14px; border-bottom: 1px solid #ddd; page-break-inside: avoid; }
    .question.grouped { border-bottom: 2px solid #999; }
    .q-header { display: flex; flex-wrap: wrap; align-items: center; gap: 5px; margin-bottom: 6px; }
    .q-num { font-weight: 700; font-size: 12pt; }
    .badge { display: inline-block; padding: 1px 7px; border-radius: 3px; font-size: 8.5pt; }
    .badge-type { background: #e3f2fd; color: #1565c0; }
    .badge-diff { background: #fff3e0; color: #e65100; }
    .badge-marks { background: #f3e5f5; color: #6a1b9a; }
    .badge-sub { background: #e8f5e9; color: #1b5e20; }
    .q-text { margin-bottom: 8px; line-height: 1.6; }
    .q-stem { background: #f5f5f5; padding: 8px 10px; border-radius: 4px; margin-bottom: 8px; }
    .q-images { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 8px; }
    .q-images img { max-width: 240px; max-height: 180px; object-fit: contain; border: 1px solid #ddd; border-radius: 3px; }
    .q-text img.latex-inline, .q-answer img.latex-inline { vertical-align: middle; }
    .q-options { margin-bottom: 8px; }
    .option { padding: 2px 0 2px 10px; line-height: 1.5; }
    .option.correct { color: #1b5e20; font-weight: 600; }
    .opt-label { font-weight: 700; margin-right: 4px; }
    .tick { color: #2e7d32; font-weight: 700; }
    .q-answer { background: #f0fdf4; border: 1px solid #bbf7d0; border-radius: 4px; padding: 7px 11px; margin-top: 6px; line-height: 1.6; font-size: 10.5pt; }
    .correct-ans { color: #1b5e20; font-weight: 600; }
    .blank-row, .match-row, .order-row { padding: 2px 0; }
    .sub-questions { padding-left: 14px; border-left: 3px solid #90caf9; margin-top: 8px; }
    .sub-questions .question { border-bottom: 1px dashed #ddd; margin-bottom: 12px; padding-bottom: 10px; }
    @media print {
      body { padding: 12mm 15mm; }
      .question { page-break-inside: avoid; }
    }
  </style>
</head>
<body>
  <h1>${title}</h1>
  <div class="subtitle">${subtitle}</div>
  <hr />
  ${bodyHtml}
</body>
</html>`;

    const blob = new Blob([pageHtml], { type: "text/html;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const win = window.open(url, "_blank");
    if (!win) {
      URL.revokeObjectURL(url);
      toast.error("Popup blocked — please allow popups and try again");
      return;
    }
    win.addEventListener("load", () => {
      setTimeout(() => {
        if (!win.closed && !win.__printed) win.print();
        URL.revokeObjectURL(url);
      }, 4000);
    });
  };

  // ── derived ──
  const currentQ = questions[currentIndex] || null;
  /** Slots the generator could not make type-faithful, or whose visual is a placeholder. */
  const questionNeedsReview = (q) =>
    Boolean(q?.answer_data?.needs_review) || (q?.type_issues || []).length > 0;
  const questionHasPlaceholderVisual = (q) =>
    q?.image_generation_meta?.visual_status === "placeholder";
  const currentBlocked = questionHasPlaceholderVisual(currentQ);
  /** Every question that still needs attention, so the reviewer does not have to step through all of them. */
  const issueList = questions
    .map((q, idx) => {
      const parts = q?.sub_questions?.length ? q.sub_questions : [q];
      const reasons = [];
      if (questionHasPlaceholderVisual(q) || questionNeedsFigure(q)) reasons.push("figure");
      if (questionNeedsReview(q) || parts.some(questionNeedsReview)) reasons.push("flagged");
      if (parts.some((p) => !questionHasAnswer(p))) reasons.push("answer");
      if (parts.some((p) => !stripHtml(p?.explanation))) reasons.push("explanation");
      return reasons.length ? { idx, reasons } : null;
    })
    .filter(Boolean);
  const approvedCount = approvedSet.size;
  const isGrouped = currentQ?.type === "grouped";
  const subQuestions = isGrouped ? currentQ?.sub_questions || [] : [];
  // Comprehension passage: grouped question with passage_type="comprehension" or long question_text (>400 chars)
  const alreadyImported = (jobData?.imported_question_ids || []).length > 0;

  const isComprehensionPassage =
    isGrouped &&
    (currentQ?.passage_type === "comprehension" ||
      (currentQ?.question_text?.length ?? 0) > 400);

  // Which paper this job is a mock of. exam_title is null on most imports, so fall back to
  // the source PDF's filename — the reviewer needs to know what is being created.
  const paperName =
    jobData?.exam_title?.trim() ||
    (jobData?.source_filename || "")
      .replace(/\.pdf$/i, "")
      .replace(/[-_]+/g, " ")
      .replace(/\s+/g, " ")
      .trim();

  // ════════════════════════════════════════════════════════════════════════
  // RENDER — LIST MODE
  // ════════════════════════════════════════════════════════════════════════

  if (mode === "list") {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
          Similar Papers
        </Typography>
        {similarityDialog}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Review and import AI-generated similar paper questions into the Question Bank.
        </Typography>

        <Paper sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "center" }}>
            <TextField
              size="small"
              label="Search"
              placeholder="Paper name, subject, school..."
              value={jobsSearchInput}
              onChange={(e) => setJobsSearchInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  updateListParams({ search: jobsSearchInput || null, page: 1 });
                }
              }}
              sx={{ minWidth: 260 }}
            />
            <Button variant="contained" onClick={() => updateListParams({ search: jobsSearchInput || null, page: 1 })}>
              Search
            </Button>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={jobsStatus}
                label="Status"
                onChange={(e) => updateListParams({ status: e.target.value || null, page: 1 })}
              >
                <MenuItem value="">All statuses</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="processing">Processing</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="failed">Failed</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Source</InputLabel>
              <Select
                value={jobsSource}
                label="Source"
                onChange={(e) => updateListParams({ source: e.target.value || null, page: 1 })}
              >
                <MenuItem value="">All sources</MenuItem>
                <MenuItem value="ai">AI</MenuItem>
                <MenuItem value="bank">Bank</MenuItem>
                <MenuItem value="mixed">Mixed</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Difficulty</InputLabel>
              <Select
                value={jobsDifficulty}
                label="Difficulty"
                onChange={(e) => updateListParams({ difficulty: e.target.value || null, page: 1 })}
              >
                <MenuItem value="">All difficulties</MenuItem>
                <MenuItem value="Easy">Easy</MenuItem>
                <MenuItem value="Medium">Medium</MenuItem>
                <MenuItem value="Hard">Hard</MenuItem>
                <MenuItem value="Mixed">Mixed</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Subject</InputLabel>
              <Select
                value={filterOptions.subjects.some((s) => String(s.id) === jobsSubjectId) ? jobsSubjectId : ""}
                label="Subject"
                onChange={(e) => updateListParams({ subject_id: e.target.value || null, page: 1 })}
              >
                <MenuItem value="">
                  All subjects{filterOptions.total ? ` (${filterOptions.total})` : ""}
                </MenuItem>
                {filterOptions.subjects.map((s) => (
                  <MenuItem key={s.id} value={String(s.id)}>
                    {s.name} ({s.jobs_count ?? 0})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel>School</InputLabel>
              <Select
                value={filterOptions.schools.some((s) => String(s.id) === jobsSchoolId) ? jobsSchoolId : ""}
                label="School"
                onChange={(e) => updateListParams({ school_id: e.target.value || null, page: 1 })}
              >
                <MenuItem value="">
                  All schools{filterOptions.total ? ` (${filterOptions.total})` : ""}
                </MenuItem>
                {filterOptions.schools.map((s) => (
                  <MenuItem key={s.id} value={String(s.id)}>
                    {s.name} ({s.jobs_count ?? 0})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Verification</InputLabel>
              <Select
                value={jobsVerificationStatus}
                label="Verification"
                onChange={(e) => updateListParams({ verification_status: e.target.value || null, page: 1 })}
              >
                <MenuItem value="">All verification</MenuItem>
                <MenuItem value="passed">Passed</MenuItem>
                <MenuItem value="issues_found">Issues found</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Imported</InputLabel>
              <Select
                value={jobsImported}
                label="Imported"
                onChange={(e) => updateListParams({ imported: e.target.value || null, page: 1 })}
              >
                <MenuItem value="">All jobs</MenuItem>
                <MenuItem value="yes">Imported to bank</MenuItem>
                <MenuItem value="no">Not imported</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Paper>

        <Paper sx={{ width: "100%" }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>ID</strong></TableCell>
                  <TableCell><strong>Original Paper</strong></TableCell>
                  <TableCell><strong>Subject</strong></TableCell>
                  <TableCell><strong>Source</strong></TableCell>
                  <TableCell><strong>Questions</strong></TableCell>
                  <TableCell><strong>Difficulty</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell><strong>Linked</strong></TableCell>
                  <TableCell><strong>Verification</strong></TableCell>
                  <TableCell><strong>Generation Info</strong></TableCell>
                  <TableCell><strong>Created</strong></TableCell>
                  <TableCell align="right"><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {jobsLoading
                  ? [...Array(5)].map((_, i) => (
                      <TableRow key={i}>
                        {[...Array(12)].map((_, j) => (
                          <TableCell key={j}><Skeleton /></TableCell>
                        ))}
                      </TableRow>
                    ))
                  : jobs.length === 0
                  ? (
                    <TableRow>
                      <TableCell colSpan={12} align="center" sx={{ py: 6 }}>
                        <Typography color="text.secondary">No similar papers generated yet.</Typography>
                      </TableCell>
                    </TableRow>
                  )
                  : jobs.map((job) => (
                      <TableRow key={job.id} hover>
                        <TableCell>{job.id}</TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {job.exam_title || job.source_filename || `Import #${job.import_id}`}
                          </Typography>
                          {job.school_name && (
                            <Typography variant="caption" color="text.secondary">
                              {job.school_name}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {job.exam_subject && (
                            <Chip label={job.exam_subject} size="small" variant="outlined" />
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={job.source || "ai"}
                            size="small"
                            color={job.source === "bank" ? "info" : job.source === "mixed" ? "warning" : "default"}
                          />
                        </TableCell>
                        <TableCell>{job.questions_count}</TableCell>
                        <TableCell>
                          {job.difficulty && <DifficultyChip difficulty={job.difficulty} />}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={job.status}
                            size="small"
                            color={
                              job.status === "completed" ? "success"
                              : job.status === "failed" ? "error"
                              : "warning"
                            }
                          />
                          {job.imported_question_ids?.length > 0 && (
                            <Chip label="Imported" size="small" color="info" sx={{ ml: 0.5 }} />
                          )}
                        </TableCell>
                        <TableCell>
                          {(job.created_entities || []).length === 0 ? (
                            <Typography variant="caption" color="text.secondary">-</Typography>
                          ) : (
                            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 0.5 }}>
                              {job.created_entities.map((e) => (
                                <Tooltip key={`${e.entity_type}-${e.entity_id}`} title={`${CREATE_TARGET_LABELS[e.entity_type] || e.entity_type}: ${e.name}`}>
                                  <Chip
                                    label={e.name}
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                    onClick={() => navigate(CREATE_TARGET_ROUTES[e.entity_type] || "/admin/mock-exams")}
                                    sx={{ maxWidth: 180 }}
                                  />
                                </Tooltip>
                              ))}
                            </Box>
                          )}
                        </TableCell>
                        <TableCell>
                          {job.verification_status ? (
                            <Tooltip
                              title={
                                job.verification_status === "issues_found" && job.verification_report
                                  ? buildVerificationSummary(job.verification_report)
                                  : job.verification_status
                              }
                              arrow
                            >
                              <Chip
                                label={job.verification_status === "issues_found" ? "Issues found" : "Passed"}
                                size="small"
                                color={job.verification_status === "passed" ? "success" : "warning"}
                                variant="outlined"
                              />
                            </Tooltip>
                          ) : (
                            "-"
                          )}
                          <Box sx={{ mt: 0.5 }}>
                            <SimilarityChip
                              job={job}
                              height={20}
                              fontSize="0.65rem"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSimilarityTarget({ id: job.id, name: job.download_filename || job.source_filename });
                              }}
                            />
                            <WebSimilarityChip
                              job={job}
                              height={20}
                              fontSize="0.65rem"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSimilarityTarget({ id: job.id, name: job.download_filename || job.source_filename });
                              }}
                            />
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.secondary">
                            {job.generation_metadata?.style?.source || "legacy"}
                          </Typography>
                          <br />
                          <Typography variant="caption" color="text.secondary">
                            {job.generation_metadata?.images?.provider_order?.join(" -> ") || "default"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Tooltip title={new Date(job.created_at).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}>
                            <Typography variant="caption">
                              {formatDistanceToNow(new Date(job.created_at), { addSuffix: true })}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="Review & Import">
                            <span>
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => openReview(job.id)}
                                disabled={job.status !== "completed"}
                              >
                                <Visibility />
                              </IconButton>
                            </span>
                          </Tooltip>
                          {job.import_id && (
                            <Tooltip title={job.status === "failed" ? "Retry: open original import" : "View original import"}>
                              <IconButton
                                size="small"
                                color={job.status === "failed" ? "warning" : "default"}
                                onClick={() => navigate(`/admin/question-bank/import/${job.import_id}/review`)}
                              >
                                {job.status === "failed"
                                  ? <AutoAwesome fontSize="small" />
                                  : <OpenInNew fontSize="small" />}
                              </IconButton>
                            </Tooltip>
                          )}
                          {(job.imported_question_ids || []).length > 0 && (
                            <Tooltip title="Delete the questions this job saved (and anything created from them)">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => {
                                  setJobData(job);
                                  setDeleteOpen(true);
                                }}
                              >
                                <DeleteOutline fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
              </TableBody>
            </Table>
          </TableContainer>
          {!jobsLoading && (
            <TablePagination
              component="div"
              count={jobsTotal}
              page={jobsPage}
              onPageChange={(_, nextPage) => updateListParams({ page: nextPage + 1 })}
              rowsPerPage={jobsPerPage}
              onRowsPerPageChange={(e) => updateListParams({ per_page: e.target.value, page: 1 })}
              rowsPerPageOptions={[15, 25, 50]}
            />
          )}
        </Paper>
      </Box>
    );
  }

  // ════════════════════════════════════════════════════════════════════════
  // RENDER — REVIEW MODE
  // ════════════════════════════════════════════════════════════════════════

  if (reviewLoading) {
    return (
      <Box sx={{ p: 3, display: "flex", justifyContent: "center", alignItems: "center", minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!jobData) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Job not found</Alert>
        <Button sx={{ mt: 2 }} onClick={backToList}>Back to list</Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", mb: 2, gap: 1, flexWrap: "wrap" }}>
        <Button startIcon={<ArrowBack />} onClick={backToList} size="small">
          Back
        </Button>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }} noWrap title={paperName}>
            {paperName || `Job #${jobData.id}`}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Similar Paper Review — Job #{jobData.id}
            {jobData.exam_subject ? ` · ${jobData.exam_subject}` : ""}
          </Typography>
        </Box>
        <SimilarityChip
          job={jobData}
          height={24}
          fontSize="0.75rem"
          onClick={() => setSimilarityTarget({ id: jobData.id, name: paperName })}
        />
        <WebSimilarityChip
          job={jobData}
          height={24}
          fontSize="0.75rem"
          onClick={() => setSimilarityTarget({ id: jobData.id, name: paperName })}
        />
        {similarityDialog}
        {jobData.import_id && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<OpenInNew />}
            onClick={() => navigate(`/admin/question-bank/import/${jobData.import_id}/review`)}
          >
            Original Paper
          </Button>
        )}
        {jobData.import_id && (
          <Button
            size="small"
            variant="contained"
            color="secondary"
            startIcon={<AutoAwesome />}
            onClick={() => setGenOpen(true)}
          >
            Generate Similar Paper
          </Button>
        )}
      </Box>

      {/* Meta */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
          <Chip label={`Source: ${jobData.source || "ai"}`} size="small" />
          <Chip label={`Difficulty: ${jobData.difficulty || "Mixed"}`} size="small" />
          <Chip label={`${questions.length} questions`} size="small" color="primary" />
          {/* A run that came up short used to look identical to a complete one. The generator now
              records what it asked the model for against what came back, so a paper padded out with
              copies of the source is visible before anyone approves it. */}
          {jobData.stats?.count_shortfall && (
            <Tooltip
              title={`The model returned ${jobData.stats.count_shortfall.ai_returned} of ${jobData.stats.count_shortfall.requested} questions. The remaining ${jobData.stats.count_shortfall.backfilled} were filled from the source paper and may need regenerating.`}
            >
              <Chip
                label={`${jobData.stats.count_shortfall.backfilled} backfilled`}
                size="small"
                color="warning"
              />
            </Tooltip>
          )}
          <Chip
            label={`${approvedCount} approved`}
            size="small"
            color={approvedCount > 0 ? "success" : "default"}
          />
          {jobData.stats?.passage_source?.title && (
            <Chip
              size="small"
              variant="outlined"
              color="info"
              label={[
                `Passage: ${jobData.stats.passage_source.title}`,
                jobData.stats.passage_source.author,
                jobData.stats.passage_source.pages,
                jobData.stats.passage_source.kind === "ai_style" ? "AI, in style" : null,
              ]
                .filter(Boolean)
                .join(" · ")}
            />
          )}
          {jobData.stats?.types_degraded > 0 && (
            <Chip
              label={`${jobData.stats.types_degraded} need review`}
              size="small"
              color="warning"
              variant="outlined"
            />
          )}
          {jobData.stats?.visuals_placeholder > 0 && (
            <Chip
              label={`${jobData.stats.visuals_placeholder} placeholder visual${jobData.stats.visuals_placeholder === 1 ? "" : "s"}`}
              size="small"
              color="error"
              variant="outlined"
            />
          )}
          {jobData.stats?.visuals_deterministic > 0 && (
            <Chip
              label={`${jobData.stats.visuals_deterministic} drawn / ${jobData.stats.visuals_ai || 0} AI images`}
              size="small"
              variant="outlined"
            />
          )}
          {(jobData.created_entities || []).map((e) => (
            <Chip
              key={`${e.entity_type}-${e.entity_id}`}
              size="small"
              color="primary"
              variant="outlined"
              label={`${CREATE_TARGET_LABELS[e.entity_type] || e.entity_type}: ${e.name}`}
            />
          ))}
          {jobData.imported_question_ids?.length > 0 && (
            <Alert severity="info" sx={{ py: 0, flexGrow: 1 }}>
              Already imported {jobData.imported_question_ids.length} questions — later edits reach
              the mock exam / paper only after "Update … in Bank".
            </Alert>
          )}
        </Box>
      </Paper>

      {questions.length === 0 ? (
        <Alert severity="warning">No questions in this job.</Alert>
      ) : (
        <Box sx={{ display: "flex", gap: 2 }}>
          {/* ── Sidebar: question list ── */}
          <Paper sx={{ width: 260, minWidth: 260, maxHeight: "75vh", overflow: "auto", p: 1 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1, px: 1, gap: 0.5, flexWrap: "wrap" }}>
              <Typography variant="subtitle2">Questions</Typography>
              <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                <Tooltip title="Fix everything in the background: regenerate flagged questions, redraw missing figures, fill empty answers, write missing explanations, then verify again">
                  <span>
                    <Button
                      size="small"
                      variant="contained"
                      color="secondary"
                      onClick={() => startHeal()}
                      disabled={healing || healStarting || generatingAnswers || questions.length === 0}
                      startIcon={healing || healStarting ? <CircularProgress size={12} color="inherit" /> : <AutoFixHigh sx={{ fontSize: "0.9rem !important" }} />}
                      sx={{ textTransform: "none", fontSize: "0.7rem" }}
                    >
                      {healing ? "Fixing…" : "Auto-fix all"}
                    </Button>
                  </span>
                </Tooltip>
                <Tooltip title="Redraw every missing or placeholder figure in one go">
                  <span>
                    <Button
                      size="small"
                      color="secondary"
                      onClick={() => startHeal(["figures"])}
                      disabled={healing || healStarting || questions.length === 0}
                      sx={{ textTransform: "none", fontSize: "0.7rem" }}
                    >
                      Redraw figures
                    </Button>
                  </span>
                </Tooltip>
                <Tooltip title="Write AI explanations in the background for every question (and sub-question) that has none">
                  <span>
                    <Button
                      size="small"
                      color="secondary"
                      onClick={() => startHeal(["explanations"])}
                      disabled={healing || healStarting || generatingExplanation || generatingAnswers || !jobData?.import_id || questions.length === 0}
                      startIcon={<AutoAwesome sx={{ fontSize: "0.9rem !important" }} />}
                      sx={{ textTransform: "none", fontSize: "0.7rem" }}
                    >
                      Explain All
                    </Button>
                  </span>
                </Tooltip>
                <Tooltip title="Generate AI answers for every question in this paper">
                  <span>
                    <Button
                      size="small"
                      color="secondary"
                      onClick={() => handleGenerateAnswers()}
                      disabled={healing || generatingAnswers || !jobData?.id || questions.length === 0}
                      startIcon={generatingAnswers ? <CircularProgress size={12} /> : <AutoFixHigh sx={{ fontSize: "0.9rem !important" }} />}
                      sx={{ textTransform: "none", fontSize: "0.7rem" }}
                    >
                      {generatingAnswers ? "Answering…" : "Answer All"}
                    </Button>
                  </span>
                </Tooltip>
                <Button size="small" onClick={approveAll}>Approve All</Button>
                <Button size="small" color="inherit" onClick={rejectAll}>Reject All</Button>
              </Box>
            </Box>
            {healing && (
              <Alert severity="info" sx={{ mb: 1, py: 0, fontSize: "0.75rem" }}>
                Auto-fix {healState?.status === "queued" ? "queued" : `running${healState?.current ? `: ${healState.current}` : ""}`} —
                edits made now will be replaced when it finishes.
              </Alert>
            )}
            {!healing && healState?.status === "completed" && healState?.summary && (
              <Alert severity="success" sx={{ mb: 1, py: 0, fontSize: "0.75rem" }}>
                Last auto-fix: {Object.values(healState.summary).join("; ")}
              </Alert>
            )}
            {issueList.length > 0 && (
              <Box sx={{ mb: 1, px: 1 }}>
                <Typography variant="caption" color="warning.main" sx={{ fontWeight: 700, display: "block" }}>
                  {issueList.length} question{issueList.length === 1 ? "" : "s"} need attention
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.5 }}>
                  {issueList.map(({ idx, reasons }) => (
                    <Tooltip key={idx} title={`Missing / flagged: ${reasons.join(", ")}`}>
                      <Chip
                        size="small"
                        color={reasons.includes("figure") || reasons.includes("flagged") ? "warning" : "default"}
                        variant={idx === currentIndex ? "filled" : "outlined"}
                        label={`Q${idx + 1}`}
                        onClick={() => goTo(idx)}
                        sx={{ height: 20, fontSize: "0.65rem" }}
                      />
                    </Tooltip>
                  ))}
                </Box>
              </Box>
            )}
            <Divider sx={{ mb: 1 }} />
            {questions.map((q, idx) => (
              <Box
                key={idx}
                onClick={() => goTo(idx)}
                sx={{
                  p: 1,
                  mb: 0.5,
                  cursor: "pointer",
                  borderRadius: 1,
                  border: idx === currentIndex ? "2px solid #1976d2" : "1px solid #e0e0e0",
                  bgcolor: idx === currentIndex ? "#e3f2fd" : "transparent",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  "&:hover": { bgcolor: "#f5f5f5" },
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 24 }}>
                  {idx + 1}
                </Typography>
                <Box sx={{ flexGrow: 1, overflow: "hidden" }}>
                  <Typography variant="caption" noWrap>
                    {stripHtml(q.question_text).slice(0, 60) || "(no text)"}
                  </Typography>
                  <Box sx={{ display: "flex", gap: 0.5, mt: 0.3, flexWrap: "wrap" }}>
                    {q.type && (
                      <Chip
                        label={q.type}
                        size="small"
                        sx={{ height: 18, fontSize: 10, bgcolor: typeColors[q.type] || "#999", color: "#fff" }}
                      />
                    )}
                    {modifiedSet.has(idx) && (
                      <Chip label="Edited" size="small" sx={{ height: 18, fontSize: 10 }} color="warning" />
                    )}
                    {questionNeedsFigure(q) && (
                      <Tooltip title="This question asked for a diagram and none could be drawn — use AI diagram to add one">
                        <Chip label="No figure" size="small" color="error" sx={{ height: 18, fontSize: 10 }} />
                      </Tooltip>
                    )}
                    {q.topic && (
                      <Chip label={q.topic} size="small" variant="outlined" color="secondary" sx={{ height: 18, fontSize: 10 }} />
                    )}
                  </Box>
                </Box>
                {approvedSet.has(idx) ? (
                  <CheckCircle sx={{ color: "success.main", fontSize: 20 }} />
                ) : (
                  <Cancel sx={{ color: "#ccc", fontSize: 20 }} />
                )}
                <Tooltip title="Remove this extra / unwanted question">
                  <span>
                    <IconButton
                      size="small"
                      color="error"
                      disabled={questions.length <= 1}
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteQuestionAt(idx);
                      }}
                      sx={{ p: 0.25 }}
                    >
                      <DeleteOutline fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            ))}
          </Paper>

          {/* ── Main content ── */}
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            {/* Nav bar */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <IconButton size="small" disabled={currentIndex === 0} onClick={() => goTo(currentIndex - 1)}>
                <ArrowBack />
              </IconButton>
              <Typography variant="body2">
                Question {currentIndex + 1} of {questions.length}
              </Typography>
              <IconButton size="small" disabled={currentIndex === questions.length - 1} onClick={() => goTo(currentIndex + 1)}>
                <ArrowForward />
              </IconButton>
              <Box sx={{ flexGrow: 1 }} />
              <Tooltip
                title={
                  currentBlocked && !approvedSet.has(currentIndex)
                    ? "Draw a figure for this question before approving it"
                    : ""
                }
              >
                <span>
                  <Button
                    size="small"
                    variant={approvedSet.has(currentIndex) ? "contained" : "outlined"}
                    color={approvedSet.has(currentIndex) ? "success" : "inherit"}
                    disabled={currentBlocked && !approvedSet.has(currentIndex)}
                    onClick={() => toggleApproval(currentIndex)}
                  >
                    {approvedSet.has(currentIndex) ? "Approved" : "Approve"}
                  </Button>
                </span>
              </Tooltip>
              {!editMode && (
                <Tooltip
                  title={
                    originals && !originals[currentIndex]
                      ? "No original is linked to this question"
                      : "Show the original question this one was generated from, side by side"
                  }
                >
                  <span>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={originalsLoading ? <CircularProgress size={14} /> : <CompareArrows />}
                      onClick={() => openCompare(currentIndex)}
                      disabled={!!originals && !originals[currentIndex]}
                    >
                      Compare original
                    </Button>
                  </span>
                </Tooltip>
              )}
              {!editMode && (
                <Tooltip
                  title={
                    isComprehensionPassage
                      ? "Write a fresh passage and re-write every question against it"
                      : isGrouped
                      ? "Re-roll this whole question, including every part (keeps the rest of the paper)"
                      : "Re-roll just this question with the AI (keeps the rest of the paper)"
                  }
                >
                  <span>
                    <Button
                      size="small"
                      variant="outlined"
                      color="secondary"
                      startIcon={
                        regeneratingScope === "slot" ? (
                          <CircularProgress size={14} />
                        ) : (
                          <Autorenew />
                        )
                      }
                      onClick={() => handleRegenerate(currentIndex, {})}
                      disabled={regeneratingIdx !== null}
                    >
                      {regeneratingScope === "slot"
                        ? "Regenerating…"
                        : isComprehensionPassage
                        ? "Regenerate passage"
                        : isGrouped
                        ? "Regenerate question"
                        : "Regenerate"}
                    </Button>
                  </span>
                </Tooltip>
              )}
              {!editMode && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<Edit />}
                  onClick={startEdit}
                >
                  {isGrouped && subQuestions.length > 0 ? "Edit Stem" : "Edit"}
                </Button>
              )}
              {!editMode && (
                <Button
                  size="small"
                  variant="outlined"
                  color="secondary"
                  startIcon={<Add />}
                  onClick={addSubQuestionToCurrentQuestion}
                  sx={{ textTransform: "none" }}
                >
                  Add sub-question
                </Button>
              )}
              {!editMode && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<Add />}
                  onClick={addStandaloneQuestion}
                  sx={{ textTransform: "none" }}
                >
                  Add question
                </Button>
              )}
              {!editMode && (
                <Tooltip title="Remove this question from the paper">
                  <span>
                    <Button
                      size="small"
                      color="error"
                      variant="outlined"
                      startIcon={<Delete fontSize="small" />}
                      onClick={deleteCurrentQuestion}
                      disabled={questions.length <= 1}
                      sx={{ textTransform: "none" }}
                    >
                      Remove question
                    </Button>
                  </span>
                </Tooltip>
              )}
            </Box>

            {/* Question content */}
            <Paper sx={{ p: 2, border: "1px dashed #ccc", minHeight: 300 }}>
              {editMode && editDraft ? (
                /* ── Edit panel ── */
                <Box>
                  {editTarget?.type === "subquestion" && (
                    <Chip
                      label={`Editing sub-question ${currentIndex + 1}.${(editTarget.subIndex ?? 0) + 1}`}
                      size="small"
                      color="primary"
                      sx={{ mb: 1.5, fontWeight: 700 }}
                    />
                  )}
                  {editTarget?.type === "stem" && (
                    <Chip
                      label={`Editing shared stem of Q${currentIndex + 1}`}
                      size="small"
                      color="primary"
                      sx={{ mb: 1.5, fontWeight: 700 }}
                    />
                  )}
                  {Object.keys(validationErrors).length > 0 && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      {Object.values(validationErrors).join(" · ")}
                    </Alert>
                  )}
                  <Box sx={{ display: "flex", gap: 1, mb: 2, flexWrap: "wrap" }}>
                    {editTarget?.type !== "stem" && (
                      <FormControl size="small" sx={{ minWidth: 180 }} error={!!validationErrors.type}>
                        <InputLabel>Type</InputLabel>
                        <Select
                          value={
                            EDITABLE_QUESTION_TYPES.some((t) => t.value === editDraft.type)
                              ? editDraft.type
                              : "short-answer"
                          }
                          label="Type"
                          onChange={(e) => applyTypeChange(e.target.value)}
                        >
                          {EDITABLE_QUESTION_TYPES.map((t) => (
                            <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                    {editTarget?.type === "stem" && (
                      <FormControl size="small" sx={{ minWidth: 160 }}>
                        <InputLabel>Passage type</InputLabel>
                        <Select
                          value={editDraft.passage_type || ""}
                          label="Passage type"
                          onChange={(e) => updateDraftField("passage_type", e.target.value || null)}
                        >
                          <MenuItem value="">None</MenuItem>
                          <MenuItem value="comprehension">Comprehension</MenuItem>
                          <MenuItem value="context">Context</MenuItem>
                        </Select>
                      </FormControl>
                    )}
                    <FormControl size="small" sx={{ minWidth: 120 }} error={!!validationErrors.difficulty}>
                      <InputLabel>Difficulty</InputLabel>
                      <Select
                        value={editDraft.difficulty || "Medium"}
                        label="Difficulty"
                        onChange={(e) => updateDraftField("difficulty", e.target.value)}
                      >
                        <MenuItem value="Easy">Easy</MenuItem>
                        <MenuItem value="Medium">Medium</MenuItem>
                        <MenuItem value="Hard">Hard</MenuItem>
                      </Select>
                    </FormControl>
                    <TextField
                      size="small"
                      label="Marks"
                      type="number"
                      value={editDraft.default_marks ?? ""}
                      onChange={(e) => updateDraftField("default_marks", parseInt(e.target.value) || 1)}
                      sx={{ width: 80 }}
                      inputProps={{ min: 1 }}
                      error={!!validationErrors.default_marks}
                      helperText={validationErrors.default_marks}
                    />
                    <TextField
                      size="small"
                      label="Topic"
                      value={editDraft.topic ?? ""}
                      onChange={(e) => updateDraftField("topic", e.target.value)}
                      sx={{ width: 180 }}
                    />
                  </Box>
                  <TextField
                    size="small"
                    fullWidth
                    multiline
                    minRows={2}
                    label="Diagram description"
                    placeholder="Accessibility / diagram generation hint"
                    helperText="Steers AI diagram and Redraw figure — describe the layout and every value the picture must show."
                    value={editDraft.image_description ?? ""}
                    onChange={(e) => updateDraftField("image_description", e.target.value)}
                    sx={{ mb: 2 }}
                  />
                  <Box
                    sx={{
                      mb: 2,
                      "& .quill": { background: "#fff", borderRadius: 1 },
                      "& .ql-toolbar": { borderRadius: "6px 6px 0 0", background: "#f9f9f9", flexWrap: "wrap" },
                      "& .ql-container": { minHeight: 120, borderRadius: "0 0 6px 6px", fontSize: "0.95rem" },
                      "& .ql-editor": { minHeight: 120 },
                    }}
                  >
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: "block", mb: 0.5 }}>
                      {editDraft.type === "grouped"
                        ? "Passage / shared text (what students see)"
                        : "Question text (what students see)"}
                    </Typography>
                    <QuillR2Editor
                      value={editDraft.question_text || ""}
                      onChange={(html) => updateDraftField("question_text", normalizeQuillValue(html))}
                      modules={editDraft.type === "grouped" ? PASSAGE_QUILL_MODULES : QUILL_MODULES}
                      formats={editDraft.type === "grouped" ? PASSAGE_QUILL_FORMATS : QUILL_FORMATS}
                      placeholder={
                        editTarget?.type === "stem" || editDraft.type === "grouped"
                          ? "Paste the full reading passage here..."
                          : editTarget?.type === "subquestion"
                            ? "Enter sub-question text (formatting, lists, images…)"
                            : "Question stem (formatting, lists, images…)"
                      }
                      showMathTools
                    />
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: "#555", display: "block", mb: 0.75 }}>
                      Images and diagrams (students see these with the question text)
                    </Typography>
                    <input
                      type="file"
                      accept="image/png, image/jpeg, image/webp"
                      ref={editFileInputRef}
                      style={{ display: "none" }}
                      disabled={editImageUploading}
                      multiple
                      onChange={async (e) => {
                        const files = Array.from(e.target.files || []);
                        e.target.value = "";
                        for (const file of files) {
                          await uploadEditStemImage(file);
                        }
                      }}
                    />
                    {collectImageUrls(editDraft).map((url, idx) => (
                      <Box
                        key={url}
                        sx={{
                          display: "inline-block",
                          position: "relative",
                          border: "1px solid #e0e0e0",
                          borderRadius: 1,
                          overflow: "hidden",
                          width: 88,
                          height: 88,
                          mr: 1,
                          mb: 1,
                          verticalAlign: "top",
                        }}
                      >
                        <img
                          src={url}
                          alt={`img-${idx}`}
                          style={{ width: "100%", height: "100%", objectFit: "cover" }}
                          onError={(e) => { e.target.style.opacity = "0.3"; }}
                        />
                        <IconButton
                          size="small"
                          onClick={() => removeEditStemImage(url)}
                          sx={{ position: "absolute", top: 1, right: 1, bgcolor: "rgba(255,255,255,0.85)", color: "#f44336", p: 0.25 }}
                        >
                          <Close sx={{ fontSize: 14 }} />
                        </IconButton>
                        <Box sx={{ position: "absolute", bottom: 0, left: 0, right: 0, bgcolor: "rgba(0,0,0,0.5)", textAlign: "center", py: 0.2 }}>
                          <Typography variant="caption" sx={{ color: "#fff", fontSize: "0.6rem" }}>
                            {idx === 0 ? "Primary" : `#${idx + 1}`}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                    <Box
                      onDragOver={(e) => {
                        if (e.dataTransfer?.types?.includes("Files")) {
                          e.preventDefault();
                          setEditImageDropActive(true);
                        }
                      }}
                      onDragLeave={(e) => {
                        if (!e.currentTarget.contains(e.relatedTarget)) setEditImageDropActive(false);
                      }}
                      onDrop={async (e) => {
                        const file = e.dataTransfer?.files?.[0];
                        if (!file) return;
                        e.preventDefault();
                        e.stopPropagation();
                        setEditImageDropActive(false);
                        await uploadEditStemImage(file);
                      }}
                      onClick={() => !editImageUploading && editFileInputRef.current?.click()}
                      sx={{
                        mb: 1,
                        border: "1px dashed",
                        borderColor: editImageDropActive ? "primary.main" : "divider",
                        bgcolor: editImageDropActive ? "primary.50" : "grey.50",
                        borderRadius: 1.5,
                        px: 2,
                        py: 2,
                        minHeight: 88,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        textAlign: "center",
                        cursor: editImageUploading ? "not-allowed" : "pointer",
                      }}
                    >
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: "text.secondary" }}>
                          Drag and drop image here, or click to browse
                        </Typography>
                        <Typography variant="caption" color="text.disabled">
                          PNG, JPEG, or WebP
                        </Typography>
                      </Box>
                    </Box>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={editImageUploading ? <CircularProgress size={14} /> : <CloudUpload />}
                      onClick={() => editFileInputRef.current?.click()}
                      disabled={editImageUploading}
                      sx={{ textTransform: "none" }}
                    >
                      {editImageUploading ? "Uploading…" : "Add Image"}
                    </Button>
                  </Box>

                  {editTarget?.type !== "stem" && (
                    <>
                      {["multiple-choice", "multiple-select"].includes(editDraft.type) && (() => {
                        const mcqOpts = (editDraft.options || []).map((o) => ({
                          text: o.text || o.option_text || "",
                          image_url: o.image_url || null,
                        }));
                        while (mcqOpts.length < 2) mcqOpts.push({ text: "", image_url: null });
                        const mcqCorrectIndex = (editDraft.options || []).findIndex((o) => o.is_correct);
                        const mcqCorrectIndices = (editDraft.options || []).reduce(
                          (acc, o, i) => (o.is_correct ? [...acc, i] : acc),
                          []
                        );
                        const setMcqOptions = (newOpts) =>
                          setEditDraft((prev) => ({
                            ...prev,
                            options: newOpts.map((o, i) => ({
                              ...(prev.options?.[i] || {}),
                              text: o.text,
                              option_text: o.text,
                              image_url: o.image_url ?? null,
                            })),
                          }));
                        const setMcqCorrectIndex = (idx) =>
                          setEditDraft((prev) => ({
                            ...prev,
                            options: (prev.options || []).map((o, i) => ({ ...o, is_correct: i === idx })),
                          }));
                        const setMcqCorrectIndices = (idxs) =>
                          setEditDraft((prev) => ({
                            ...prev,
                            options: (prev.options || []).map((o, i) => ({ ...o, is_correct: idxs.includes(i) })),
                          }));
                        return (
                          <Box sx={{ mb: 2 }}>
                            <McqOptions
                              options={mcqOpts}
                              setOptions={setMcqOptions}
                              correctIndex={mcqCorrectIndex >= 0 ? mcqCorrectIndex : 0}
                              setCorrectIndex={setMcqCorrectIndex}
                              correctIndices={mcqCorrectIndices}
                              setCorrectIndices={setMcqCorrectIndices}
                              isMultiSelect={editDraft.type === "multiple-select"}
                            />
                            {validationErrors.options && (
                              <FormHelperText error>{validationErrors.options}</FormHelperText>
                            )}
                          </Box>
                        );
                      })()}
                      {editDraft.type === "true-false" && (
                        <Box sx={{ mb: 2 }}>
                          <TrueFalseOptions
                            value={trueFalseValueFromDraft(editDraft)}
                            onChange={(isTrue) =>
                              setEditDraft((prev) => ({
                                ...prev,
                                options: [
                                  { text: "True", option_text: "True", is_correct: !!isTrue },
                                  { text: "False", option_text: "False", is_correct: !isTrue },
                                ],
                              }))
                            }
                          />
                        </Box>
                      )}
                      {["fill-in-the-blank", "multi-fill"].includes(editDraft.type) && (() => {
                        const blanks = getCorrectBlanks(editDraft);
                        const fillMarksMode = editDraft.answer_data?.marks_mode === "fixed" ? "fixed" : "sum";
                        const fillFixedTotal = editDraft.answer_data?.marks_total ?? editDraft.default_marks ?? 1;
                        return (
                          <Box sx={{ mb: 2 }}>
                            <FillTypeMarksControls
                              marksMode={fillMarksMode}
                              onMarksModeChange={(mode) => updateFillMarksConfig(mode, fillFixedTotal)}
                              fixedTotal={fillFixedTotal}
                              onFixedTotalChange={(val) => updateFillMarksConfig("fixed", val)}
                              blankCount={blanks.length}
                            />
                            <FillInBlankEditor
                              blanks={blanks}
                              setBlanks={(correct_blanks) => patchAnswerData({ correct_blanks })}
                              isMulti={editDraft.type === "multi-fill"}
                              blankLabels={editDraft.answer_data?.blank_labels || []}
                              setBlankLabels={(blank_labels) => patchAnswerData({ blank_labels })}
                              blankMarks={editDraft.answer_data?.blank_marks || []}
                              setBlankMarks={(blank_marks) => patchAnswerData({ blank_marks })}
                              blankLabelPositions={editDraft.answer_data?.blank_label_positions || []}
                              setBlankLabelPositions={(blank_label_positions) =>
                                patchAnswerData({ blank_label_positions })
                              }
                              fillMarksMode={fillMarksMode}
                              fillFixedTotal={fillFixedTotal}
                            />
                          </Box>
                        );
                      })()}
                      {editDraft.type === "table" && (
                        <Box sx={{ mb: 2 }}>
                          <FillTypeMarksControls
                            marksMode={editDraft.answer_data?.marks_mode === "fixed" ? "fixed" : "sum"}
                            onMarksModeChange={(mode) =>
                              updateFillMarksConfig(mode, editDraft.answer_data?.marks_total ?? editDraft.default_marks ?? 4)
                            }
                            fixedTotal={editDraft.answer_data?.marks_total ?? editDraft.default_marks ?? 4}
                            onFixedTotalChange={(val) => updateFillMarksConfig("fixed", val)}
                            blankCount={(editDraft.answer_data?.correct_blanks || []).length}
                          />
                          <TableFillEditor
                            blanks={getCorrectBlanks(editDraft)}
                            setBlanks={(correct_blanks) => patchAnswerData({ correct_blanks })}
                            blankMarks={editDraft.answer_data?.blank_marks || []}
                            setBlankMarks={(blank_marks) => patchAnswerData({ blank_marks })}
                            blankLabels={editDraft.answer_data?.blank_labels || []}
                            setBlankLabels={(blank_labels) => patchAnswerData({ blank_labels })}
                            tableLayout={
                              editDraft.answer_data?.table_layout?.grid
                                ? editDraft.answer_data.table_layout
                                : DEFAULT_GRID()
                            }
                            setTableLayout={(table_layout) => patchAnswerData({ table_layout })}
                            showMathTools
                          />
                        </Box>
                      )}
                      {editDraft.type === "image-fill" && (
                        <Box sx={{ mb: 2 }}>
                          <FillTypeMarksControls
                            marksMode={editDraft.answer_data?.marks_mode === "fixed" ? "fixed" : "sum"}
                            onMarksModeChange={(mode) =>
                              updateFillMarksConfig(mode, editDraft.answer_data?.marks_total ?? editDraft.default_marks ?? 4)
                            }
                            fixedTotal={editDraft.answer_data?.marks_total ?? editDraft.default_marks ?? 4}
                            onFixedTotalChange={(val) => updateFillMarksConfig("fixed", val)}
                            blankCount={(editDraft.answer_data?.correct_blanks || []).length}
                          />
                          <ImageOverlayFillEditor
                            blanks={getCorrectBlanks(editDraft)}
                            setBlanks={(correct_blanks) => patchAnswerData({ correct_blanks })}
                            blankMarks={editDraft.answer_data?.blank_marks || []}
                            setBlankMarks={(blank_marks) => patchAnswerData({ blank_marks })}
                            blankLabels={editDraft.answer_data?.blank_labels || []}
                            setBlankLabels={(blank_labels) => patchAnswerData({ blank_labels })}
                            overlayLayout={
                              editDraft.answer_data?.overlay_layout?.image_url
                              || editDraft.answer_data?.overlay_layout?.mode
                              || (editDraft.answer_data?.overlay_layout?.blanks?.length > 0)
                                ? editDraft.answer_data.overlay_layout
                                : { image_url: "", blanks: [] }
                            }
                            setOverlayLayout={(overlay_layout) => patchAnswerData({ overlay_layout })}
                            attachedImages={collectImageUrls(editDraft)}
                          />
                        </Box>
                      )}
                      {editDraft.type === "short-answer" && (
                        <Box sx={{ mb: 2 }}>
                          <ShortAnswerEditor
                            value={editDraft.answer_data?.correct_answer_text || ""}
                            onChange={(value) => patchAnswerData({ correct_answer_text: value })}
                            showMathTools
                          />
                        </Box>
                      )}
                      {editDraft.type === "essay" && (
                        <Box sx={{ mb: 2 }}>
                          <EssayAnswer
                            value={
                              editDraft.answer_data?.model_answer
                              || editDraft.answer_data?.correct_answer_text
                              || ""
                            }
                            onChange={(value) =>
                              patchAnswerData({ model_answer: value, correct_answer_text: value })
                            }
                            showMathTools
                          />
                        </Box>
                      )}
                      {editDraft.type === "matching" && (
                        <Box sx={{ mb: 2 }}>
                          <MatchingEditor
                            options={editDraft.options || [{ text: "", match_text: "" }, { text: "", match_text: "" }]}
                            setOptions={(options) => updateDraftField("options", options)}
                          />
                        </Box>
                      )}
                      {editDraft.type === "ordering" && (
                        <Box sx={{ mb: 2 }}>
                          <OrderingEditor
                            options={editDraft.options || [{ text: "" }, { text: "" }]}
                            setOptions={(options) => updateDraftField("options", options)}
                          />
                        </Box>
                      )}
                      {editDraft.type === "drawing" && (() => {
                        const spec = editDraft.answer_data?.solution_geometry?.interactive || null;
                        const mode = spec?.kind || "free";
                        const setSpec = (s) =>
                          patchAnswerData({
                            solution_geometry: s ? { interactive: s } : null,
                          });
                        // The figure to author on: the question's own image, else its rendered figure, else an
                        // <img> in the question text. Reading image_url(s) only left dot-grid questions whose
                        // figure is rendered/embedded with "attach the question image first" and no grid.
                        const htmlImage = (String(editDraft.question_text || "").match(/<img[^>]+src=["']([^"']+)["']/i) || [])[1] || "";
                        const drawImage =
                          collectImageUrls(editDraft)[0]
                          || (Array.isArray(editDraft.render_image_urls) ? editDraft.render_image_urls.filter(Boolean)[0] : "")
                          || editDraft.original_image_url
                          || htmlImage
                          || "";
                        return (
                          <Box sx={{ mb: 2, display: "flex", flexDirection: "column", gap: 1.5 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>Answer mode</Typography>
                            <FormControl size="small" sx={{ maxWidth: 320 }}>
                              <InputLabel>Answer mode</InputLabel>
                              <Select
                                label="Answer mode"
                                value={mode}
                                onChange={(e) =>
                                  setSpec(
                                    e.target.value === "free"
                                      ? null
                                      // Keep the figure and the snap grid when switching mode — they describe the
                                      // diagram, not the answer, and re-entering them by hand was the usual reason
                                      // a re-authored question lost its grid.
                                      : { kind: e.target.value, ...(spec?.image_url ? { image_url: spec.image_url } : {}), ...(spec?.grid ? { grid: spec.grid } : {}) }
                                  )
                                }
                              >
                                <MenuItem value="free">Free draw (AI vision)</MenuItem>
                                <MenuItem value="shade">Shade regions / pie chart (tap to fill)</MenuItem>
                                <MenuItem value="points">Place points / reflect (tap grid)</MenuItem>
                                <MenuItem value="rect">Draw rectangle (area match)</MenuItem>
                                <MenuItem value="lines">Draw lines (symmetry / mirror)</MenuItem>
                                <MenuItem value="polygon">Draw shape by area (triangle / square)</MenuItem>
                                <MenuItem value="glyph">Place / reflect / rotate letters</MenuItem>
                                <MenuItem value="segments">Join points (segments)</MenuItem>
                                <MenuItem value="pictogram">Pictogram (fill icons)</MenuItem>
                                <MenuItem value="drag-shape">Drag-copy shape (translation)</MenuItem>
                                <MenuItem value="reflect">Reflect shape (auto-computed)</MenuItem>
                                <MenuItem value="rotate">Rotate shape (auto-computed)</MenuItem>
                                <MenuItem value="clock">Clock hands (set the time)</MenuItem>
                                <MenuItem value="marker">Arrow / bar marker (value on axis)</MenuItem>
                                <MenuItem value="tessellation-grid">Dot-grid tessellation (join dot edges)</MenuItem>
                                <MenuItem value="cut-drag">Cut shape &amp; drag pieces (rearrange)</MenuItem>
                                <MenuItem value="angle">Draw an angle (given vertex, graded in degrees)</MenuItem>
                                <MenuItem value="tally">Tally marks (fill a chart cell)</MenuItem>
                                <MenuItem value="construction">Construction board (ruler, compass, protractor)</MenuItem>
                              </Select>
                            </FormControl>
                            {mode === "free" ? (
                              <Typography variant="caption" color="text.secondary">
                                Free draw: the student draws on the pad; grading uses AI vision. Pick an interactive
                                mode above to grade instantly with no AI.
                              </Typography>
                            ) : (
                              <InteractiveDiagramEditor
                                kind={mode}
                                spec={spec}
                                onChange={setSpec}
                                imageUrl={drawImage}
                              />
                            )}
                          </Box>
                        );
                      })()}
                    </>
                  )}
                  {editTarget?.type !== "stem" && (
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5, gap: 1, flexWrap: "wrap" }}>
                        <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary" }}>
                          Explanation (optional)
                        </Typography>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={generatingExplanation ? <CircularProgress size={12} /> : <AutoAwesome sx={{ fontSize: "0.9rem !important" }} />}
                          onClick={() => handleGenerateExplanation({ fromEdit: true })}
                          disabled={generatingExplanation || improving}
                          sx={{ py: 0.25, px: 1, fontSize: "0.75rem", textTransform: "none" }}
                        >
                          {generatingExplanation ? "Generating…" : "AI Generate"}
                        </Button>
                      </Box>
                      <Box
                        sx={{
                          "& .ql-toolbar": { borderRadius: "6px 6px 0 0" },
                          "& .ql-container": { minHeight: 110, borderRadius: "0 0 6px 6px", fontSize: "0.9rem" },
                        }}
                      >
                        <QuillR2Editor
                          value={editDraft.explanation || ""}
                          onChange={(html) => updateDraftField("explanation", normalizeQuillValue(html))}
                          modules={EXPL_QUILL_MODULES}
                          formats={EXPL_QUILL_FORMATS}
                          placeholder="Explain the correct answer for student review..."
                          style={{ minHeight: 140 }}
                          showMathTools
                        />
                      </Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.75 }}>
                        Shown to students after they complete the exam. Use the image button in the toolbar to embed explanation images.
                      </Typography>
                    </Box>
                  )}
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Button
                      variant="contained"
                      startIcon={savingEdit ? <CircularProgress size={16} color="inherit" /> : <Save />}
                      onClick={saveEdit}
                      disabled={savingEdit}
                    >
                      {savingEdit ? "Saving…" : "Save"}
                    </Button>
                    <Button variant="outlined" startIcon={<Close />} onClick={cancelEdit} disabled={savingEdit}>
                      Cancel
                    </Button>
                  </Box>
                </Box>
              ) : currentQ ? (
                /* ── Display panel ── */
                <Box>
                  {/* Meta chips */}
                  <Box sx={{ display: "flex", gap: 1, mb: 2, flexWrap: "wrap", alignItems: "center" }}>
                    <Chip
                      label={`Q${currentIndex + 1}`}
                      size="small"
                      sx={{ fontWeight: 700 }}
                    />
                    {currentQ.type && (
                      <Chip
                        label={QUESTION_TYPES.find((t) => t.value === currentQ.type)?.label || currentQ.type}
                        size="small"
                        sx={{ bgcolor: typeColors[currentQ.type] || "#999", color: "#fff" }}
                      />
                    )}
                    {currentQ.difficulty && <DifficultyChip difficulty={currentQ.difficulty} />}
                    {currentQ.default_marks && (
                      <Chip label={`${currentQ.default_marks} mark${currentQ.default_marks > 1 ? "s" : ""}`} size="small" />
                    )}
                    {currentQ.topic && (
                      <Chip label={currentQ.topic} size="small" variant="outlined" color="secondary" />
                    )}
                    {questionNeedsReview(currentQ) && (
                      <Tooltip
                        title={
                          [
                            ...(currentQ.type_issues || []),
                            ...(currentQ.answer_data?.review_reasons || []),
                          ].join(", ") ||
                          "The AI could not produce a type-faithful answer for this question"
                        }
                      >
                        <Chip label="Needs review" size="small" color="warning" />
                      </Tooltip>
                    )}
                    {questionHasPlaceholderVisual(currentQ) && (
                      <Tooltip title="No visual could be generated — use AI diagram to draw one">
                        <Chip label="Placeholder visual" size="small" color="error" />
                      </Tooltip>
                    )}
                    {!isGrouped && (
                      <>
                        <Button
                          size="small"
                          variant="outlined"
                          color="secondary"
                          startIcon={improving ? <CircularProgress size={14} /> : <AutoFixHigh />}
                          disabled={improving || generatingExplanation || generatingAnswers}
                          onClick={() => handleAiImprove({ type: "standalone", index: currentIndex })}
                          sx={{ textTransform: "none" }}
                        >
                          {improving ? "Working…" : "AI Improve"}
                        </Button>
                        <Tooltip title="Generate AI answer for this question">
                          <span>
                            <Button
                              size="small"
                              variant="outlined"
                              color="secondary"
                              startIcon={generatingAnswers ? <CircularProgress size={14} /> : <AutoFixHigh />}
                              disabled={generatingAnswers || generatingExplanation || improving}
                              onClick={() => handleGenerateAnswers([currentIndex])}
                              sx={{ textTransform: "none" }}
                            >
                              {generatingAnswers ? "Generating…" : "AI Answer"}
                            </Button>
                          </span>
                        </Tooltip>
                        <Tooltip title="Generate AI explanation for this question">
                          <span>
                            <Button
                              size="small"
                              variant="outlined"
                              color="secondary"
                              startIcon={generatingExplanation ? <CircularProgress size={14} /> : <AutoAwesome />}
                              disabled={generatingExplanation || improving || generatingAnswers}
                              onClick={() => handleGenerateExplanation({ type: "standalone", index: currentIndex })}
                              sx={{ textTransform: "none" }}
                            >
                              {generatingExplanation ? "Generating…" : "AI Explanation"}
                            </Button>
                          </span>
                        </Tooltip>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={generatingDiagram ? <CircularProgress size={14} /> : <AutoAwesome />}
                          disabled={generatingDiagram}
                          onClick={() => handleAiGenerateDiagram({ type: "standalone" })}
                        >
                          AI diagram
                        </Button>
                        {slotHasFigure(currentQ) && (
                          <Tooltip title="Draw a new figure for this question, replacing the one carried over from the original paper">
                            <span>
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={redrawingIdx === currentIndex ? <CircularProgress size={14} /> : <Autorenew />}
                                disabled={redrawingIdx !== null || generatingDiagram}
                                onClick={() => handleRedrawVisual(currentIndex)}
                                sx={{ textTransform: "none" }}
                              >
                                {redrawingIdx === currentIndex ? "Redrawing…" : "Redraw figure"}
                              </Button>
                            </span>
                          </Tooltip>
                        )}
                      </>
                    )}
                    {usesOriginalFigure(currentQ) && (
                      <Tooltip title="This figure is still the original paper's — redraw it so it matches the new question">
                        <Chip label="Same as original" size="small" color="warning" variant="outlined" />
                      </Tooltip>
                    )}
                  </Box>

                  {/* Grouped question */}
                  {isGrouped ? (
                    <Box>
                      {/* Stem */}
                      <Paper sx={{ p: 2, mb: 2, bgcolor: "#f5f5f5" }}>
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1, gap: 1, flexWrap: "wrap" }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Typography variant="subtitle2" color="text.secondary">
                              {isComprehensionPassage ? "Comprehension Passage" : "Shared Stem"}
                            </Typography>
                            {isComprehensionPassage && (
                              <Chip label="Comprehension" size="small" color="info" variant="outlined" sx={{ height: 20, fontSize: "0.65rem" }} />
                            )}
                          </Box>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<Edit />}
                              onClick={() => startEditStem(currentIndex)}
                              sx={{ textTransform: "none" }}
                            >
                              Edit Stem
                            </Button>
                            <Tooltip
                              title={
                                isComprehensionPassage
                                  ? "Write a fresh passage and re-write every question against it"
                                  : "Write a fresh shared stem and re-write every part against it"
                              }
                            >
                              <span>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={
                                    regeneratingScope === "passage" ? <CircularProgress size={14} /> : <Autorenew />
                                  }
                                  disabled={regeneratingIdx !== null}
                                  onClick={() => handleRegenerate(currentIndex, { scope: "passage" })}
                                  sx={{ textTransform: "none" }}
                                >
                                  {regeneratingScope === "passage"
                                    ? "Regenerating…"
                                    : isComprehensionPassage
                                    ? "Regenerate passage"
                                    : "Regenerate stem"}
                                </Button>
                              </span>
                            </Tooltip>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={generatingDiagram ? <CircularProgress size={14} /> : <AutoAwesome />}
                              disabled={generatingDiagram}
                              onClick={() => handleAiGenerateDiagram({ type: "stem" })}
                            >
                              AI diagram
                            </Button>
                            {slotHasFigure(currentQ) && (
                              <Tooltip title="Draw a new stem figure, replacing the one carried over from the original paper">
                                <span>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={redrawingIdx === currentIndex ? <CircularProgress size={14} /> : <Autorenew />}
                                    disabled={redrawingIdx !== null || generatingDiagram}
                                    onClick={() => handleRedrawVisual(currentIndex)}
                                    sx={{ textTransform: "none" }}
                                  >
                                    {redrawingIdx === currentIndex ? "Redrawing…" : "Redraw figure"}
                                  </Button>
                                </span>
                              </Tooltip>
                            )}
                            {usesOriginalFigure(currentQ) && (
                              <Chip label="Same as original" size="small" color="warning" variant="outlined" sx={{ height: 20, fontSize: "0.65rem" }} />
                            )}
                          </Box>
                        </Box>
                        {isComprehensionPassage ? (
                          <PassageWithLineNumbers text={currentQ.question_text || ""} />
                        ) : (
                          <HtmlWithLatex html={currentQ.question_text || ""} />
                        )}
                        {collectImageUrls(currentQ).map((url) => (
                          <Box key={url} sx={{ mt: 1 }}>
                            <img
                              src={url}
                              alt="Question"
                              style={{ maxWidth: "100%", maxHeight: 300, borderRadius: 4 }}
                            />
                          </Box>
                        ))}
                      </Paper>
                      {/* Sub-questions */}
                      {subQuestions.map((sq, si) => (
                        <Paper key={si} sx={{ p: 2, mb: 1.5, border: "1px solid #e0e0e0" }}>
                          <Box sx={{ display: "flex", gap: 1, mb: 1, alignItems: "center", flexWrap: "wrap" }}>
                            <Chip label={`${currentIndex + 1}.${si + 1}`} size="small" />
                            {sq.type && (
                              <Chip
                                label={QUESTION_TYPES.find((t) => t.value === sq.type)?.label || sq.type}
                                size="small"
                                sx={{ bgcolor: typeColors[sq.type] || "#999", color: "#fff", fontSize: 11 }}
                              />
                            )}
                            {sq.default_marks && (
                              <Chip label={`${sq.default_marks}m`} size="small" />
                            )}
                            {questionNeedsReview(sq) && (
                              <Tooltip
                                title={
                                  [
                                    ...(sq.type_issues || []),
                                    ...(sq.answer_data?.review_reasons || []),
                                  ].join(", ") ||
                                  "The AI could not produce a type-faithful answer for this question"
                                }
                              >
                                <Chip label="Needs review" size="small" color="warning" />
                              </Tooltip>
                            )}
                            {questionNeedsReview(sq) && (
                              <Tooltip
                                title={
                                  [
                                    ...(sq.type_issues || []),
                                    ...(sq.answer_data?.review_reasons || []),
                                  ].join(", ") ||
                                  "The AI could not produce a type-faithful answer for this question"
                                }
                              >
                                <Chip label="Needs review" size="small" color="warning" />
                              </Tooltip>
                            )}
                            <Button
                              size="small"
                              variant="outlined"
                              color="secondary"
                              startIcon={improving ? <CircularProgress size={13} /> : <AutoFixHigh />}
                              disabled={improving || generatingExplanation || generatingAnswers}
                              onClick={() => handleAiImprove({ type: "sub", parentIndex: currentIndex, subIndex: si })}
                              sx={{ textTransform: "none" }}
                            >
                              {improving ? "Working…" : "AI Improve"}
                            </Button>
                            <Tooltip title="Replace just this part with a new question about the passage above">
                              <span>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={
                                    regeneratingSub === si ? <CircularProgress size={13} /> : <Autorenew />
                                  }
                                  disabled={regeneratingIdx !== null || improving}
                                  onClick={() => handleRegenerate(currentIndex, { scope: "sub", subIndex: si })}
                                  sx={{ textTransform: "none" }}
                                >
                                  {regeneratingSub === si ? "Regenerating…" : "Regenerate"}
                                </Button>
                              </span>
                            </Tooltip>
                            <Tooltip title="Generate AI answers for all parts of this grouped question">
                              <span>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="secondary"
                                  startIcon={generatingAnswers ? <CircularProgress size={13} /> : <AutoFixHigh />}
                                  disabled={generatingAnswers || generatingExplanation || improving}
                                  onClick={() => handleGenerateAnswers([currentIndex])}
                                  sx={{ textTransform: "none" }}
                                >
                                  AI Answer
                                </Button>
                              </span>
                            </Tooltip>
                            <Tooltip title="Generate AI explanation for this sub-question">
                              <span>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="secondary"
                                  startIcon={generatingExplanation ? <CircularProgress size={13} /> : <AutoAwesome />}
                                  disabled={generatingExplanation || improving || generatingAnswers}
                                  onClick={() => handleGenerateExplanation({ type: "sub", parentIndex: currentIndex, subIndex: si })}
                                  sx={{ textTransform: "none" }}
                                >
                                  AI Explanation
                                </Button>
                              </span>
                            </Tooltip>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={generatingDiagram ? <CircularProgress size={14} /> : <AutoAwesome />}
                              disabled={generatingDiagram}
                              onClick={() => handleAiGenerateDiagram({ type: "sub", subIndex: si })}
                            >
                              AI diagram
                            </Button>
                            {slotHasFigure(sq) && (
                              <Tooltip title="Draw a new figure for this part, replacing the one carried over from the original paper">
                                <span>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={redrawingIdx === currentIndex ? <CircularProgress size={14} /> : <Autorenew />}
                                    disabled={redrawingIdx !== null || generatingDiagram}
                                    onClick={() => handleRedrawVisual(currentIndex, si)}
                                    sx={{ textTransform: "none" }}
                                  >
                                    Redraw figure
                                  </Button>
                                </span>
                              </Tooltip>
                            )}
                            {usesOriginalFigure(sq) && (
                              <Chip label="Same as original" size="small" color="warning" variant="outlined" sx={{ height: 20, fontSize: "0.65rem" }} />
                            )}
                            <Button
                              size="small"
                              variant="contained"
                              startIcon={<Edit />}
                              onClick={() => startEditSubQuestion(currentIndex, si)}
                              sx={{ textTransform: "none" }}
                            >
                              Edit
                            </Button>
                            <Tooltip title="Remove this sub-question">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => deleteSubQuestion(currentIndex, si)}
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                          <QuestionPaperLayout
                            question={sq}
                            questionNumber={`${currentIndex + 1}.${si + 1}`}
                            showAnswers={false}
                          />
                          {questionHasAnswer(sq) && (
                            <Box sx={{ mt: 1, p: 1.5, bgcolor: "#f0fdf4", borderRadius: 1, border: "1px solid #bbf7d0" }}>
                              <Typography variant="caption" color="success.dark" sx={{ fontWeight: 600 }}>
                                Answer
                              </Typography>
                              <QuestionAnswerDisplay question={sq} />
                            </Box>
                          )}
                          {sq.explanation && String(sq.explanation).replace(/<[^>]*>/g, "").trim() && (
                            <Box sx={{ mt: 1, p: 1.5, bgcolor: "#f8fafc", borderRadius: 1, border: "1px solid #e2e8f0" }}>
                              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: "block", mb: 0.5 }}>
                                Explanation
                              </Typography>
                              <HtmlWithLatex html={sq.explanation} />
                            </Box>
                          )}
                        </Paper>
                      ))}
                      <Box sx={{ display: "flex", justifyContent: "center", mt: 0.5, mb: 1 }}>
                        <Button
                          size="small"
                          variant="outlined"
                          color="secondary"
                          startIcon={<Add />}
                          onClick={() => addSubQuestionAt(currentIndex)}
                          sx={{ textTransform: "none" }}
                        >
                          Add sub-question
                        </Button>
                      </Box>
                    </Box>
                  ) : (
                    /* Standalone question */
                    <Box>
                      <QuestionPaperLayout
                        question={currentQ}
                        questionNumber={currentIndex + 1}
                        showAnswers={false}
                      />
                      {questionHasAnswer(currentQ) && (
                        <Box sx={{ mt: 2, p: 1.5, bgcolor: "#f0fdf4", borderRadius: 1, border: "1px solid #bbf7d0" }}>
                          <Typography variant="caption" color="success.dark" sx={{ fontWeight: 600 }}>
                            Answer
                          </Typography>
                          <QuestionAnswerDisplay question={currentQ} />
                        </Box>
                      )}
                      {currentQ.explanation && String(currentQ.explanation).replace(/<[^>]*>/g, "").trim() && (
                        <Box sx={{ mt: 2, p: 1.5, bgcolor: "#f8fafc", borderRadius: 1, border: "1px solid #e2e8f0" }}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: "block", mb: 0.5 }}>
                            Explanation
                          </Typography>
                          <HtmlWithLatex html={currentQ.explanation} />
                        </Box>
                      )}
                    </Box>
                  )}
                  {currentQ.original_image_url && (
                    <Box sx={{ mt: 2, pt: 2, borderTop: "1px dashed #ddd" }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                        Original visual (reference only — never saved with this question)
                      </Typography>
                      <img
                        src={currentQ.original_image_url}
                        alt="Original question visual"
                        style={{ maxWidth: 260, maxHeight: 180, borderRadius: 4, opacity: 0.75 }}
                      />
                    </Box>
                  )}
                </Box>
              ) : (
                <Typography color="text.secondary">No question selected</Typography>
              )}
            </Paper>

            {/* Footer actions */}
            <Box sx={{ display: "flex", gap: 1, mt: 2, justifyContent: "flex-end", flexWrap: "wrap" }}>
              {jobData?.download_filename && jobData?.import_id && (
                <Tooltip title="Download the generated paper PDF (formatted like the original)">
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={downloadingPdf ? <CircularProgress size={16} color="inherit" /> : <Download />}
                    onClick={handleDownloadBackendPdf}
                    disabled={downloadingPdf}
                  >
                    {downloadingPdf ? "Downloading…" : "Download PDF"}
                  </Button>
                </Tooltip>
              )}
              <Tooltip title="Download all questions with answers as printable PDF">
                <Button
                  variant="outlined"
                  startIcon={<Download />}
                  onClick={() => handleDownloadPdf(false)}
                  disabled={questions.length === 0}
                >
                  Download Q&amp;A PDF (All)
                </Button>
              </Tooltip>
              {approvedCount > 0 && (
                <Tooltip title="Download only approved questions with answers as printable PDF">
                  <Button
                    variant="outlined"
                    color="success"
                    startIcon={<Download />}
                    onClick={() => handleDownloadPdf(true)}
                  >
                    Download Q&amp;A PDF (Approved {approvedCount})
                  </Button>
                </Tooltip>
              )}
              <Button
                variant="outlined"
                onClick={saveDraft}
                disabled={submitting || savingDraft}
                startIcon={savingDraft ? <CircularProgress size={16} color="inherit" /> : null}
              >
                {savingDraft ? "Saving…" : "Save Draft"}
              </Button>
              <Tooltip title="Save the APPROVED questions to the bank, then create a Mock Exam, Past Paper, or Worksheet from them">
                <span>
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<PostAdd />}
                    endIcon={<ArrowDropDown />}
                    onClick={(e) => setCreateMenuAnchor(e.currentTarget)}
                    disabled={approvedCount === 0}
                  >
                    Create As ({approvedCount})
                  </Button>
                </span>
              </Tooltip>
              {jobData?.already_saved && (
                <Tooltip title="Delete the questions this job saved (and anything created from them)">
                  <Button variant="outlined" color="error" onClick={() => setDeleteOpen(true)}>
                    Delete generated
                  </Button>
                </Tooltip>
              )}
              <Button
                variant="contained"
                color="success"
                disabled={submitting || approvedCount === 0}
                onClick={() => setSaveDialogOpen(true)}
              >
                {submitting ? (
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                ) : null}
                {alreadyImported
                  ? `Update ${approvedCount} Question${approvedCount !== 1 ? "s" : ""} in Bank`
                  : `Import ${approvedCount} Question${approvedCount !== 1 ? "s" : ""} to Bank`}
              </Button>
            </Box>
          </Box>
        </Box>
      )}

      {/* Compare with original — read-only, original (left) vs generated (right) */}
      <Dialog open={compareOpen} onClose={() => setCompareOpen(false)} maxWidth="xl" fullWidth>
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, fontWeight: 700 }}>
          Compare with original
          <Box sx={{ flexGrow: 1 }} />
          <IconButton size="small" disabled={compareIndex === 0} onClick={() => goToCompare(compareIndex - 1)}>
            <ArrowBack />
          </IconButton>
          <Typography variant="body2" sx={{ minWidth: 130, textAlign: "center" }}>
            Question {compareIndex + 1} of {questions.length}
          </Typography>
          <IconButton
            size="small"
            disabled={compareIndex >= questions.length - 1}
            onClick={() => goToCompare(compareIndex + 1)}
          >
            <ArrowForward />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {originalsLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2 }}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                  <Typography variant="subtitle2" sx={{ color: "text.secondary", fontWeight: 700 }}>
                    Original
                  </Typography>
                  {originals?.[compareIndex] && (
                    <Chip
                      size="small"
                      variant="outlined"
                      label={
                        originals[compareIndex].origin === "bank"
                          ? `Bank #${originals[compareIndex].source_question_id}`
                          : "Source paper"
                      }
                    />
                  )}
                </Box>
                {originals?.[compareIndex] ? (
                  <ReadOnlyQuestionView
                    question={originals[compareIndex].question}
                    questionNumber={compareIndex + 1}
                  />
                ) : (
                  <Alert severity="info" sx={{ mt: 1 }}>
                    No original linked — this question was generated without a source question.
                  </Alert>
                )}
              </Paper>
              <Paper variant="outlined" sx={{ p: 2, border: "2px solid", borderColor: "secondary.main" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                  <Typography variant="subtitle2" sx={{ color: "secondary.main", fontWeight: 700 }}>
                    Generated
                  </Typography>
                  {modifiedSet.has(compareIndex) && <Chip label="Edited" size="small" color="warning" />}
                  {approvedSet.has(compareIndex) && <Chip label="Approved" size="small" color="success" />}
                </Box>
                <ReadOnlyQuestionView question={questions[compareIndex]} questionNumber={compareIndex + 1} />
              </Paper>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCompareOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* AI Improve — compare & accept (parity with QuestionBankQuestionEditDialog) */}
      <Dialog open={aiImproveOpen} onClose={() => setAiImproveOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>AI suggestion — compare and accept</DialogTitle>
        <DialogContent dividers>
          <AiAcceptSections value={aiAccept} onChange={setAiAccept} />
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2 }}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, color: "text.secondary" }}>
                Original
              </Typography>
              {aiOriginal ? (
                <>
                  <QuestionPaperLayout question={aiOriginal} questionNumber={1} showAnswers showAdminHints={false} />
                  <AiExplanationPreview html={aiOriginal.explanation} />
                </>
              ) : null}
            </Paper>
            <Paper variant="outlined" sx={{ p: 2, border: "2px solid", borderColor: "secondary.main" }}>
              <Typography variant="subtitle2" sx={{ mb: 1, color: "secondary.main" }}>
                AI suggestion — preview of what will be applied
              </Typography>
              {aiSuggestion ? (() => {
                const merged = mergeAiSuggestion(aiOriginal, aiSuggestion, aiAccept);
                return (
                  <>
                    <QuestionPaperLayout question={merged} questionNumber={1} showAnswers showAdminHints={false} />
                    <AiExplanationPreview html={merged.explanation} />
                  </>
                );
              })() : null}
            </Paper>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAiImproveOpen(false)}>Dismiss</Button>
          <Button
            variant="contained"
            color="secondary"
            disabled={!aiSuggestion || !Object.values(aiAccept).some(Boolean)}
            onClick={applyAiSuggestion}
          >
            Accept AI Suggestion
          </Button>
        </DialogActions>
      </Dialog>

      {/* AI Generate Diagram — preview & upload (same as Import Review) */}
      <Dialog open={diagramDialogOpen} onClose={() => !imageUploadingDiagram && setDiagramDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>AI Generated Diagram</DialogTitle>
        <DialogContent dividers>
          {generatedDiagram?.image_url || generatedDiagram?.svg ? (
            <Box>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  width: "100%",
                  minHeight: 280,
                  overflow: "auto",
                  backgroundColor: "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {generatedDiagram.image_url ? (
                  <img
                    src={generatedDiagram.image_url}
                    alt="Generated diagram"
                    style={{ width: "100%", maxWidth: 520, height: "auto", display: "block" }}
                  />
                ) : (
                  <div
                    style={{ width: "100%", maxWidth: 520 }}
                    dangerouslySetInnerHTML={{
                      __html: generatedDiagram.svg.replace(
                        /<svg /,
                        '<svg style="width:100%;height:auto;display:block;" '
                      ),
                    }}
                  />
                )}
              </Paper>
              {generatedDiagram.verified === false && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  The vision check could not confirm this figure matches the question — read it before adding.
                </Alert>
              )}
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block", textAlign: "center" }}>
                Diagram shows blank spaces for students to fill in. Answers are shown separately below the question.
              </Typography>
            </Box>
          ) : (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <CircularProgress />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Generating diagram…
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDiagramDialogOpen(false)} disabled={imageUploadingDiagram}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="info"
            onClick={applyGeneratedDiagram}
            disabled={(!generatedDiagram?.image_url && !generatedDiagram?.svg) || imageUploadingDiagram}
            startIcon={imageUploadingDiagram ? <CircularProgress size={16} color="inherit" /> : <AutoAwesome />}
          >
            {imageUploadingDiagram ? "Uploading…" : "Add Diagram to Question"}
          </Button>
        </DialogActions>
      </Dialog>

      <GenerateSimilarPaperDialog
        open={genOpen}
        onClose={() => setGenOpen(false)}
        importId={jobData?.import_id}
        title={jobData?.exam_title || jobData?.source_filename}
        questionCount={questions.length || jobData?.count || undefined}
        defaultCategoryId={saveCategoryId || ""}
        subject={jobData?.exam_subject || ""}
      />

      {/* ── Create As menu ── */}
      <Menu
        anchorEl={createMenuAnchor}
        open={Boolean(createMenuAnchor)}
        onClose={() => setCreateMenuAnchor(null)}
      >
        <MenuItem onClick={() => openCreateDialog("mock_exam")}>Mock Exam</MenuItem>
        <MenuItem onClick={() => openCreateDialog("paper")}>Past Paper</MenuItem>
        <MenuItem onClick={() => openCreateDialog("worksheet")}>Worksheet</MenuItem>
      </Menu>

      <SaveToBankDestinationDialog
        open={createOpen}
        busy={creating}
        onClose={() => setCreateOpen(false)}
        onSubmit={handleCreateEntity}
        approvedCount={approvedSet.size}
        description={`${approvedSet.size} approved question${approvedSet.size === 1 ? "" : "s"} are saved to the Question Bank first, then a draft ${(CREATE_TARGET_LABELS[createTarget] || "").toLowerCase()} is created from them. You'll be redirected to finish its settings.`}
        destination={createTarget || "mock_exam"}
        onDestinationChange={(target) => {
          setCreateTarget(target);
          fetchEntityLookups(target);
        }}
        form={createForm}
        onFormChange={setCreateForm}
        categoryId={saveCategoryId}
        onCategoryChange={setSaveCategoryId}
        lookups={{
          formats: lookupFormats,
          schoolYears: lookupSchoolYears,
          topics: lookupTopics,
          schools: lookupSchools,
          loading: lookupsLoading,
        }}
      />

      <JobDeleteGeneratedDialog
        open={deleteOpen}
        job={jobData}
        busy={deleting}
        onClose={() => setDeleteOpen(false)}
        onConfirm={handleDeleteGenerated}
        nounSingular="generated question"
        listLabel="Similar Papers"
        regenerateHint="Generate Similar Paper"
      />
      <Dialog
        open={saveDialogOpen}
        onClose={() => !submitting && setSaveDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{alreadyImported ? "Update the Question Bank" : "Import to Question Bank"}</DialogTitle>
        <DialogContent dividers>
          <Alert severity="info" sx={{ mb: 2 }}>
            {alreadyImported ? (
              <>
                These questions are already in the question bank. Your edits — text, images, answers
                and explanations — will be written onto those same questions, so any mock exam, paper
                or worksheet built from this job picks them up.
              </>
            ) : (
              <>
                {approvedCount} question{approvedCount !== 1 ? "s" : ""} will be added as new question
                bank questions.
              </>
            )}
          </Alert>
          <CategorySaveFields
            categoryId={saveCategoryId}
            onCategoryChange={setSaveCategoryId}
            sourceType={saveSourceType}
            onSourceTypeChange={setSaveSourceType}
            disabled={submitting}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveDialogOpen(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleConfirm}
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {submitting ? (alreadyImported ? "Updating…" : "Importing…") : alreadyImported ? "Update bank" : "Import"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SimilarPaperReview;
